import {
  JACKPOT_VALUE_TO_ID,
  BLANK_SYMBOL_ID,
  ZEUS_DOUBLE_ID,
  ZEUS_MYSTERY_ID,
  ZEUS_COLLECT_ID,
  CD_MULT_ID,
  CM_MINOR_ID,
  CM_MINI_ID,
  CM_MAJOR_ID,
  COIN_SYMBOL_IDS,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import StorageManager from "../../../utils/StorageManager";
import { fsDiag } from "../../../utils/fsDiag";
import { COMMAND_KIND, kindOfCommand } from "../../../utils/spinCommands";
import { PROVIDER_CONFIG } from "./providerConfig";
import {
  goldenGambleCell,
  goldenGambleRevealCell,
  goldenPlainScatterCells,
} from "../../../Games/Shared/goldenMysteryStaging";
import { requestJson } from "../../requestJson";
import { moneyDiag } from "../../../utils/moneyTrace";
import { freeSpinsCapForBet } from "../../../Games/Shared/freeSpinOutroPopup";
// import { parseHoldAndWinState } from "./holdAndWinState";
import {
  parseHoldAndWinState,
  toReelRow,
  triggerCellsOnReels,
} from "./holdAndWinState";
import { jackpotCoinSkin } from "../../../Games/HoldAndWin/jackpotCoin";
import { COIN_JACKPOT_SYMBOL_ID, COIN_SYMBOL_ID } from "../../../SymbolData";
import { Settings } from "../../../settings";
import { assignCoinValues } from "../../../Games/Shared/coinValues";

// Parse bonus screen symbol: "0" → 0, "coin_10" → 20, "coin_25" → 27 (jackpot)
function parseBonusSymbol(symbolId) {
  if (typeof symbolId === "number") return symbolId;
  if (typeof symbolId !== "string") return 0;

  if (symbolId.startsWith("coin_")) {
    const coinValue = parseInt(symbolId.slice(5), 10);
    if (isNaN(coinValue)) return 0;
    // Optional chaining because jackpots are per GAME: Mad Labs has none, so
    // SymbolData no longer exports the map at all. This function is live —
    // InitGame and Spin both reach it — and only a "coin_" prefixed id gets
    // this far, which this game never sends. Guarded rather than assumed.
    const jackpotId = JACKPOT_VALUE_TO_ID?.[coinValue];
    if (jackpotId !== undefined) {
      return jackpotId;
    }
    return 10 + coinValue;
  }

  const n = parseInt(symbolId, 10);
  return isNaN(n) ? 0 : n;
}

// ── Free Spins trigger detection (GDD §8) ──
//
// Same mechanism Zeus used for its Pot Collection trigger, with the trigger
// symbols swapped: scan the landed screen, and when the trigger is complete
// manufacture ONE win carrying every trigger position. Only the symbols
// change — the shape of the win, where it is injected, and what consumes it
// downstream are all identical.
//
// Two differences from Zeus worth knowing:
//   1. The trigger is TWO symbols, not one. Two SC plus one SC1, and SC1 only
//      ever lands on reel 4. Counting "any scatter >= 3" would fire on three
//      SC, which the math never produces and which is not a trigger.
//   2. It is position-constrained. Scatters outside their allowed reels are
//      ignored, so a symbol that should not be there cannot manufacture a
//      trigger.
//
// Returns [] unless the FULL trigger is on the board — an incomplete board
// (2 SC and no SC1) is anticipation, not a trigger, and must inject nothing.
function scanBoardScatters(screen) {
  const cfg = PROVIDER_CONFIG.FREE_SPINS_TRIGGER;
  const scatters = [];
  const gambleScatters = [];
  if (!cfg || !Array.isArray(screen)) return { scatters, gambleScatters };

  for (let reel = 0; reel < screen.length; reel++) {
    const reelData = screen[reel];
    if (!Array.isArray(reelData)) continue;
    for (let row = 0; row < reelData.length; row++) {
      const raw = reelData[row];
      const id = typeof raw === "string" ? parseInt(raw, 10) : raw;
      if (isNaN(id)) continue;
      if (id === cfg.SCATTER_ID && cfg.SCATTER_REELS.includes(reel)) {
        scatters.push([reel, row]);
      } else if (
        id === cfg.GAMBLE_SCATTER_ID &&
        cfg.GAMBLE_SCATTER_REELS.includes(reel)
      ) {
        gambleScatters.push([reel, row]);
      }
    }
  }
  return { scatters, gambleScatters };
}

// ── A trigger can be split across the board AND the feature ──
//
// The scan above only ever looked at the screen, and the Golden Mystery's
// scatters are not on it: a B2 cell keeps symbol 10 whatever stands inside it.
// So a round with one SC on the board, a second SC from the feature and SC1
// from the feature — three scatters, which is a complete trigger — counted as
// one SC and no SC1 and produced nothing.
//
// Observed on round 1451830 (2026-09-04). It landed BLANK, so no free spins
// were owed and the miscount cost only the presentation: one scatter animated
// where three were showing. On an AWARDING scatter it is worse — the round
// enters the feature with two of its three scatters never acknowledged.
//
// The two sources are counted TOGETHER against the same thresholds, and the
// order is preserved: plain scatters first, gamble scatters last. Every caller
// depends on that — buildGambleScatterReveal reads the LAST entry as SC1's
// position, and so does the synthetic win.
//
// @param   {object} round The adapted round, with `reels` and `storage`.
// @returns {Array<[number,number]>} [] unless the FULL trigger is present.
function collectAllTriggerPositions(round) {
  const cfg = PROVIDER_CONFIG.FREE_SPINS_TRIGGER;
  if (!cfg) return [];

  const board = scanBoardScatters(round?.reels);
  const featureScatters = goldenPlainScatterCells(round).map((c) => [
    c.reel,
    c.row,
  ]);
  const featureGamble = goldenGambleRevealTarget(round);

  const scatters = [...board.scatters, ...featureScatters];
  const gambleScatters = [
    ...board.gambleScatters,
    ...(featureGamble ? [featureGamble.position] : []),
  ];

  if (
    scatters.length < cfg.REQUIRED_SCATTERS ||
    gambleScatters.length < cfg.REQUIRED_GAMBLE_SCATTERS
  ) {
    return [];
  }

  return [...scatters, ...gambleScatters];
}

// ── The gamble scatter can also land INSIDE the Golden Mystery ──
//
// GDD §7's resolution loop ends on "Gamble Scatter present? -> start Gamble
// Round", and the feature is explicitly a feature rather than a mode: it runs
// in the base game, and a cell may respin onto SC1 exactly as the board may
// land it. The award is the same thing — spins plus a starting level.
//
// The board scan above cannot see it and must not be made to: the SCREEN holds
// B2 at those coords, not SC1, because the cell is a Golden Mystery window with
// the scatter standing inside it. So the second source is the feature script,
// where the server writes the package on the cell itself.
//
// Returns the same three fields the board path produces, so both feed one
// trigger and nothing downstream has to know which one fired.
//
// @returns {{position:[number,number],value:number,tier:number}|null}
function goldenGambleTriggerAward(round) {
  const cell = goldenGambleCell(round);
  if (!cell) return null;
  return {
    position: [cell.reel, cell.row],
    value: cell.fsCount,
    tier: cell.fsLevel,
  };
}

// ── The SHOW's twin of the function above ──
//
// Same cell, no award filter. GDD §8: SC1 "reveals its outcome and, unless
// BLANK, hands off to the Gamble Round" — so the doors-and-strip runs whatever
// it lands on, and only entering the feature is gated on the award.
//
// Split out because using the AWARD finder for both is why a blank gamble
// scatter dropped by the feature played nothing at all: no award meant no
// position, no position meant no reveal, and the player watched a third scatter
// arrive and then simply sit there. Observed live on round 1451830.
//
// @returns {{position:[number,number],value:number,tier:number}|null}
function goldenGambleRevealTarget(round) {
  const cell = goldenGambleRevealCell(round);
  if (!cell) return null;
  return {
    position: [cell.reel, cell.row],
    value: cell.fsCount || 0,
    tier: cell.fsLevel || 0,
  };
}

// Mirror of Zeus's synthetic coin win. `isScatter` instead of `isCoin` is the
// only meaningful change: useWinController already routes an isScatter win to
// the free-spins intro exactly as it routes an isCoin win to Pot Collection —
// the flag was plumbed through the shared hook long ago and PopiPlay simply
// never set it. win is 0 because the trigger spin itself pays nothing; the
// win exists so the trigger positions get animated and the intro can open.
function buildSyntheticScatterWin(positions) {
  if (!positions.length) return null;
  return {
    id: null,
    win: 0,
    positions,
    symbol: null,
    isScatter: true,
  };
}

// ── Is there a gamble in this game? ──
//
// FEATURES_CONFIG.GAMBLE.ENABLED is the ONE switch. Off, a free-spins trigger
// is just its scatters: no SC1 reveal sequence, no gamble package to land on,
// and the intro reload comes from the server rather than localStorage.
// Cleopatra's Fire has no gamble; Mad Labs had it on.
function isGambleEnabled() {
  return !!FEATURES_CONFIG.GAMBLE?.ENABLED;
}

// ── The trigger's scatter entry: the server's when it sent one ──
//
// Cleopatra's Fire PAYS its scatters (`["scatter", 40, cells]`, 3 = 2x bet),
// so that entry is real money and must stay. Only when the server sent no
// scatter entry — Mad Labs, whose trigger paid through other lines — is the
// 0-pay synthetic one appended, exactly as before. Either way there is ONE
// scatter entry, marked isScatter, for the win presentation to key on.
function withTriggerScatterWin(wins, positions) {
  const list = wins || [];
  if (list.some((w) => w?.isScatter)) return list;
  const synthetic = buildSyntheticScatterWin(positions);
  return synthetic ? [...list, synthetic] : list;
}

// ── Cleopatra's Fire Hold & Win trigger: the coins' entry ──
//
// The scatter trigger's twin, for the coins. A trigger spin enters Hold & Win
// (state `respin`) and pays nothing for its coins — their values are paid
// inside the feature — so this 0-pay entry exists only to carry the trigger
// coins' positions: the win presentation lights them after any line win and
// then opens the Hold & Win intro, exactly as it does for Pot Collection's
// `isCoin` trigger line. The positions are storage.new_cells, which on the
// trigger step are the coins as they stand on the 5x4 screen (contract §8.2).
// ── The coins on the board, from the SYMBOLS ──
// `c` and `cj` wherever they stand on the 5x4 screen. The fallback for the
// trigger coins' line when the server's storage.new_cells is missing or empty
// (Vladimir, 2026-10-01: the symbols are the source of truth) — without a
// coin line the presentation never opens the Hold & Win intro.
function scanBoardCoins(screen) {
  const positions = [];
  if (!Array.isArray(screen)) return positions;
  screen.forEach((reelData, reel) => {
    if (!Array.isArray(reelData)) return;
    reelData.forEach((raw, row) => {
      const id = typeof raw === "string" ? parseInt(raw, 10) : raw;
      if (COIN_SYMBOL_IDS.includes(id)) positions.push([reel, row]);
    });
  });
  return positions;
}

function withTriggerCoinWin(wins, positions) {
  const list = wins || [];
  if (!positions?.length || list.some((w) => w?.isCoin)) return list;
  return [
    ...list,
    { id: null, win: 0, positions, symbol: null, isCoin: true },
  ];
}

// ── Cleopatra's Fire: jackpot coins on the trigger screen ──
//
// Base/free-spin coins carry no value — except the TRIGGER coins, whose
// points are in storage.new_cells (contract §8.2, §14.3). A trigger coin
// whose points the math makes a jackpot (options.jackpots) is drawn as the
// jackpot coin: symbol 16 (`cj`) on the round's reels, and its skin recorded
// per cell for the landing to put on (round.jackpotCoinSkins, "reel_row").
function markJackpotCoins(round, rawNewCells, jackpots) {
  if (!round?.reels || !Array.isArray(rawNewCells) || !jackpots) return;
  for (const [reel, row, points, kind] of rawNewCells) {
    if (kind !== "coin") continue;
    const skin = jackpotCoinSkin(points, jackpots);
    if (!skin || !Array.isArray(round.reels[reel])) continue;
    round.reels[reel] = round.reels[reel].map((id, r) =>
      r === row ? COIN_JACKPOT_SYMBOL_ID : id,
    );
    round.jackpotCoinSkins = { ...(round.jackpotCoinSkins || {}), [`${reel}_${row}`]: skin };
  }
}

/**
 * The payload SC1's reveal lands on — what the player actually won.
 *
 * Two sources, in order of trust:
 *
 *   outcome.storage.gamble_scatter_values   [[reel,row], value, tier]
 *       The API stating the result outright. Captured live on round 1415621.
 *   features.fs_gamble.current              [value, tier]
 *       The last rung of the ladder. Same numbers, no position — so the
 *       position falls back to the SC1 cell the board scan already found.
 *
 * `collectAllTriggerPositions` appends the gamble scatters after the
 * plain ones, so the LAST entry is SC1. That is the only ordering assumption
 * here, and it is the same one the synthetic win relies on.
 *
 * @returns {object|null} `{position, value, tier, variants}`, or null when the
 *   response carries no award — a board that looks like a trigger but has no
 *   package behind it must not fabricate one.
 */
/**
 * Has this round hit the win cap?
 *
 * ── Server first, arithmetic as the backup ──
 *
 * Exactly the split Pot Collection uses (see the winLimitReached IIFE in
 * detectFeatureState): if the backend says so, that is the answer. It has not
 * been seen sending a flag for free spins yet, so the fallback carries it —
 * and because the fallback reads `balance.game`, which is already our
 * totalWin, a reload recomputes the same answer with nothing in localStorage.
 *
 * @param   {object} apiResponse
 * @param   {number} totalWin  the round total, i.e. balance.game
 * @returns {boolean}
 */
function detectWinLimitReached(apiResponse, totalWin) {
  const storage = apiResponse?.outcome?.storage || {};
  if (storage.win_limit_reached) return true;

  const cap = winCapFor(apiResponse);
  if (cap === null) return false;
  return totalWin >= cap;
}

/**
 * The most this round can pay, in currency — or null when it cannot be known.
 *
 * @param   {object} apiResponse
 * @returns {number|null}
 */
function winCapFor(apiResponse) {
  return freeSpinsCapForBet(apiResponse?.outcome?.bet || 0);
}

/**
 * Did a CLOSED free-spins round hit the cap? Answered from its stored record.
 *
 * The live path asks detectWinLimitReached, which reads the response. A
 * completion reload has no response to read — the API has already returned to
 * base — so the record answers instead.
 *
 * The stored flag wins when present: it was stamped from the closing spin and
 * so carries the SERVER's own `win_limit_reached`, which no recomputation can
 * reproduce. The recompute is the fallback, and it uses the ROUND's stake
 * rather than the live bet, because the player may have changed bet before
 * reloading.
 *
 * @param   {object} record  StorageManager.loadFreeSpinsRound()
 * @returns {boolean}
 */
function storedWinLimitReached(record) {
  if (record?.winLimitReached) return true;
  const cap = freeSpinsCapForBet(record?.bet || 0);
  if (cap === null) return false;
  return (record?.totalWin || 0) >= cap;
}

/**
 * The round total, clamped to the cap.
 *
 * The counter is where the cap is ENFORCED, exactly as Pot Collection does it
 * — PotCollectionEndOfBonusEngine clamps every coin against the remaining
 * headroom rather than trusting the sum. Done HERE because this is the only
 * place that holds both the total and the bet: freeSpinsSlice cannot read
 * game.bet from inside a reducer.
 *
 * The server should already be sending a capped figure. This makes the client
 * agree if it does not, and costs nothing when it does.
 *
 * @param   {object} apiResponse
 * @param   {number} totalWin
 * @returns {number}
 */
function capRoundTotal(apiResponse, totalWin) {
  const cap = winCapFor(apiResponse);
  return cap === null ? totalWin : Math.min(totalWin, cap);
}

function buildGambleScatterReveal(
  features,
  outcome,
  triggerPositions,
  goldenAward = null,
) {
  const stored = outcome?.storage?.gamble_scatter_values;
  const gamble = parseGambleState(features);

  let position = null;
  let value = null;
  let tier = null;

  if (Array.isArray(stored) && stored.length >= 3) {
    [position, value, tier] = stored;
  } else if (Array.isArray(gamble?.current)) {
    [value, tier] = gamble.current;
  } else if (goldenAward) {
    // ── The package the Golden Mystery cell landed on ──
    //
    // Third rather than first because the two above are the board's own
    // record and, when a round has both, they are the more direct statement.
    // In practice they do not compete: the golden award is only looked up when
    // the board scan found no SC1 at all.
    //
    // Read straight off the cell, so no inference is needed here — the server
    // wrote `[coord, "12", fs_count, fs_level]` and both numbers are the ones
    // the reveal and the gamble screen want.
    ({ value, tier } = goldenAward);
  } else {
    // ── The gamble-less award: gold goes straight to free spins (GDD §9) ──
    //
    // Such a response may carry neither gamble_scatter_values nor fs_gamble
    // — the package exists only as the round itself: `freespins_issued` is
    // the count, and at ENTRY `fs_state.level` names the package. The two
    // scales agree numerically — entry levels are 1/3/5 (GDD §6 packages at
    // levels 0/2/4, and fs_state.level is 1-indexed) and the package tiers
    // are 1/3/5. Guarded to exactly those three values so a mid-round level
    // can never masquerade as a package.
    const issued = features?.freespins_issued;
    const entryLevel = features?.fs_state?.level;
    if (
      typeof issued === "number" &&
      issued > 0 &&
      [1, 3, 5].includes(entryLevel)
    ) {
      value = issued;
      tier = entryLevel;
    }
  }

  if (!Array.isArray(position) && triggerPositions.length) {
    position = triggerPositions[triggerPositions.length - 1];
  }

  if (!Array.isArray(position)) return null;

  // ── No award anywhere: the reveal happened and showed the BLANK ──
  //
  // SC1 landing with nothing behind it is a real outcome (GDD §8: the reveal
  // is purple/red/gold/BLANK — seen live 2026-08-18, round 1420795). The
  // sequence still plays in full; it lands on the empty window, tier 0.
  if (typeof tier !== "number") {
    tier = 0;
    value = 0;
  }

  return {
    position,
    value: typeof value === "number" ? value : 0,
    tier,
    // The pool the wheel may offer next. Empty once the ladder is closed,
    // which is exactly what the gamble dialog reads to decide whether a
    // further gamble is on the table.
    variants: gamble?.variants ?? [],
  };
}

// ── A trigger is an AWARD, not a picture ──
//
// SC1 can reveal BLANK (GDD §8): the board then shows the scatter with
// nothing behind it, and symbols alone would arm a round that does not
// exist. The server's own evidence decides — an open gamble ladder
// (fs_gamble) or an issued package (`freespins_issued`, which also covers
// the gamble-less gold award of GDD §9).
function hasFreeSpinAwardEvidence(features) {
  if (parseGambleState(features)?.current) return true;
  return (
    typeof features?.freespins_issued === "number" &&
    features.freespins_issued > 0
  );
}

// ── Bonus buy prices ──
//
// Returns { level: multiplier } for every level the API advertises. Raw API
// values are hundredths, so 8000 means 80x the current bet.
//
// Deliberately shape-tolerant. Zeus sent `feature_multipliers.bonus_buy` as
// either a bare number or an object keyed "1"/"2". Mad Labs has FOUR tiers
// (GDD §10, and window.__OPTIONS__.math.rtp lists freespin_buy_1..4) and its
// prices did not arrive in the Zeus location — the buy cards rendered 0.00.
// Rather than guess one key, every known spelling is tried and whatever is
// found wins. Unknown levels are simply absent from the map; the UI treats a
// missing price as "server decides" instead of blocking the purchase.
const BONUS_BUY_LEVELS = [1, 2, 3, 4];
const BONUS_BUY_KEYS = ["bonus_buy", "freespin_buy", "buy_bonus"];

// Returns { levels, featureName }.
//
// featureName is the key the prices were actually found under, and it is sent
// back as `purchased_feature` on the buy request. It must NOT be hardcoded:
// Zeus called the feature "bonus_buy", Mad Labs advertises "freespin_buy"
// (window.__OPTIONS__.math.rtp lists freespin_buy_1..4), and sending the wrong
// name is rejected by the API with 203 invalid_options. Deriving it from the
// same payload that carries the prices keeps the two in step by construction.
function parseBonusBuyMultipliers(featureOptions) {
  const out = {};
  let featureName = null;
  if (!featureOptions) return { levels: out, featureName };

  const sources = [featureOptions.feature_multipliers, featureOptions].filter(
    Boolean,
  );

  const add = (level, raw, base) => {
    const n = typeof raw === "string" ? parseFloat(raw) : raw;
    if (typeof n !== "number" || !isFinite(n) || n <= 0) return;
    if (out[level] !== undefined) return;
    out[level] = n / 100;
    if (base && !featureName) featureName = base;
  };

  for (const src of sources) {
    for (const base of BONUS_BUY_KEYS) {
      const grouped = src[base];
      if (grouped && typeof grouped === "object") {
        for (const level of BONUS_BUY_LEVELS) {
          add(level, grouped[level] ?? grouped[String(level)], base);
        }
      } else if (grouped !== undefined) {
        // Bare number — the single-tier Zeus shape. That is level 1.
        add(1, grouped, base);
      }
      // Flat spelling: bonus_buy_1, freespin_buy_2, …
      for (const level of BONUS_BUY_LEVELS) {
        add(level, src[`${base}_${level}`], base);
      }
    }
    // Zeus's second tier had its own key rather than a level suffix.
    add(2, src.bonus_buy_extra, "bonus_buy");
  }

  return { levels: out, featureName };
}

// ── Every buy feature's prices ──
//
// Cleopatra's Fire sells TWO features, each in two levels (init,
// 2026-09-30): feature_multipliers = { bonus_buy: {"1": 3500, "2": 7000},
// freespin_buy: {"1": 3000, "2": 5000} } — Hold & Win and free spins.
// parseBonusBuyMultipliers keeps one feature's levels only, so this keeps
// them all: { featureName: { level: multiplier } }, hundredths -> × bet.
function parseBonusBuyFeatures(featureOptions) {
  const out = {};
  const src = featureOptions?.feature_multipliers;
  if (!src || typeof src !== "object") return out;
  for (const base of BONUS_BUY_KEYS) {
    const grouped = src[base];
    if (!grouped || typeof grouped !== "object") continue;
    for (const level of BONUS_BUY_LEVELS) {
      const raw = grouped[level] ?? grouped[String(level)];
      const n = typeof raw === "string" ? parseFloat(raw) : raw;
      if (typeof n !== "number" || !isFinite(n) || n <= 0) continue;
      if (!out[base]) out[base] = {};
      out[base][level] = n / 100;
    }
  }
  return out;
}

// ── Free-spin gamble ──
//
// `features.fs_gamble` carries the whole gamble round (GDD §9), and every
// response that has it uses the same two lists:
//
//   values    the ladder so far. Each entry is [spinCount, tier], and the LAST
//             entry is the package the player owns right now.
//   variants  every pair the NEXT gamble can produce. Shrinks as the ladder
//             climbs, and is empty once the round is settled.
//
// Tier codes seen live are 0 (lose) / 1 / 3 / 5. Verified against
// dev.popiplay.dev/api/MadLabs/24570, round 1418104:
//
//   buy      values [[9,1]]              variants [[0,0],[7,3],[8,3],[9,3],[8,5],[9,5],[10,5]]
//   gamble   values [[9,1],[7,3]]        variants [[0,0],[8,5],[9,5],[10,5]]
//   gamble   values [[9,1],[7,3],[0,0]]  variants []  state closed
//   collect  freespins_issued 9          state freespins
export const GAMBLE_ACTIONS = {
  GAMBLE: "gamble_freespins",
  COLLECT: "collect_freespins",
};

const LOSE_TIER = 0;

/**
 * `features.fs_gamble` → the shape the UI reads.
 *
 * Returns null when the response carries no gamble at all, so a caller can
 * tell "no gamble round" from "a gamble round with nothing left to win".
 */
function parseGambleState(features) {
  const src = features?.fs_gamble;
  if (!src) return null;

  const values = Array.isArray(src.values) ? src.values : [];
  const variants = Array.isArray(src.variants) ? src.variants : [];
  const current = values.length ? values[values.length - 1] : null;

  return {
    values,
    variants,
    // What the player owns now, split out so nothing downstream has to know
    // that the ladder is read from its end.
    current,
    spins: Array.isArray(current) ? current[0] : 0,
    tier: Array.isArray(current) ? current[1] : null,
    // A further gamble is only offered while the server still lists outcomes.
    canGamble: variants.length > 0,
    // Tier 0 is the lose rung. Landing on it forfeits the bonus outright.
    lost: Array.isArray(current) && current[1] === LOSE_TIER,
  };
}

// Convert API screen (string IDs) → numeric symbol IDs through SYMBOL_MAP
function normalizeScreenData(screenData) {
  if (!screenData || !Array.isArray(screenData)) return screenData;
  const map = PROVIDER_CONFIG.SYMBOL_MAP;
  return screenData.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      const n = parseBonusSymbol(symbolId);
      return map[n] !== undefined ? map[n] : n;
    });
  });
}

// Web Worker factory
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

// ── Inline spin worker — the API call, off the main thread ─────────────────
//
// ── No runtime CDN ──
//
// This used to open by pulling an HTTP library from a public CDN with
// importScripts, on an unpinned URL. Three things were wrong with that, in
// order of how much they cost:
//
//   1. A blocked or slow CDN — an operator's network, a corporate proxy, a
//      captive portal, a dropped connection — means the game cannot spin at
//      all. Not "spins and shows an error": the worker dies on its first line,
//      before `onmessage` is ever defined, so the message it is sent is never
//      answered by anything.
//   2. The URL is unpinned, so whatever that host publishes is what players
//      execute. A third party can change the code in this game without us.
//   3. It was buying nothing. The worker needs one JSON POST with two headers,
//      which `fetch` has done natively in every browser this game supports for
//      years. The main thread does not use a library either — see requestJson
//      above.
//
// Do not reintroduce a runtime download on this path.
//
// ── The one behavioural difference, and how it is handled ──
//
// A library that REJECTS on a non-2xx hangs the body off `error.response.data`;
// fetch resolves instead and leaves the status on the response. So the two
// branches that needed — a 200 carrying `errors`, and a 4xx carrying `errors` —
// are one branch here: a body with an `errors` array is the server saying no,
// whatever status it rode in on. That is the shape userInterfaceSlice keys the
// popup off, so it has to stay exactly this.
//
// Every path still answers with either the payload or `{error: ...}`, because
// the caller's promise settles on nothing else.
const spinWorkerCode = `
  onmessage = async (event) => {
    const { bet, token, apiUrl, csrfTokenName, csrfTokenValue, identifier, command = "spin", purchasedFeature, purchasedFeatureLevel, options } = event.data;

    if (!bet || !token || !apiUrl || !csrfTokenName || !csrfTokenValue) {
      postMessage({ error: "PopiPlay: Spin API call failed: Missing required parameters" });
      return;
    }

    try {
      // Build request body
      let payloadOptions = { bet: bet, ...(options || {}) };
      if (purchasedFeature) {
        payloadOptions.purchased_feature = purchasedFeature;
      }
      if (purchasedFeatureLevel) {
        payloadOptions.purchased_feature_level = String(purchasedFeatureLevel);
      }
      const requestBody = { command: command, options: payloadOptions };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          [csrfTokenName]: csrfTokenValue,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      // Read the body before judging the status: an error response carries the
      // codes the popup needs, and throwing on the status would discard them.
      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        data = null;
      }

      if (data && Array.isArray(data.errors) && data.errors.length > 0) {
        postMessage({ error: data });
        return;
      }

      if (!response.ok) {
        postMessage({ error: "PopiPlay: Spin API call failed: HTTP " + response.status });
        return;
      }

      if (!data) {
        postMessage({ error: "PopiPlay: Spin API call failed: Invalid response structure" });
        return;
      }

      postMessage(data);

    } catch (error) {
      postMessage({ error: "PopiPlay: Spin API call failed: " + ((error && error.message) || error) });
    }
  };
`;

// The RAW server numbers for one response, in the money trace's channel — so
// every `[$]` state change can be checked against what the server actually
// sent. See utils/moneyTrace.
const traceServerMoney = (command, data) => {
  const fr = data?.free_rounds;
  moneyDiag(
    `server:${command}`,
    `wallet=${data?.balance?.wallet ?? "-"} game=${data?.balance?.game ?? "-"}` +
      ` outcome.win=${data?.outcome?.win ?? "-"} state=${data?.flow?.state ?? "-"}` +
      (fr
        ? ` | fr=${fr.status ?? "-"} ${fr.performed ?? "-"}/${fr.count ?? "-"}` +
          ` fr.balance=${data?.balance?.free_rounds ?? "-"}`
        : ""),
  );
};

// ─────────────────────────────────────────────
// PopiPlay RGS — 3 Orbs of Zeus
// ─────────────────────────────────────────────

class PopiPlayRGS {
  constructor() {
    this.api_base_url = window.__OPTIONS__?.api || "";
    this.worker = null;
    this.currentToken = null;
    this.lines = null;
    // Replay mode (docs/replay-history-spec.md §0). Step-cursor fields
    // (replayStepIds/replayMaxStepId/replayStepCursor/replayLastStepId/
    // replayBet) are populated by fetchReplayInit() and consumed only by
    // _replaySpin()/_nextReplayStepId() below — self-contained on this
    // object so spin()'s replay branch needs no new params threaded through
    // the shared rgsPlay/getSpinParams call chain every live spin also uses.
    this.replayMode = false;
    this.replayUrl = null;
    this.replayStepIds = null;
    this.replayMaxStepId = null;
    this.replayStepCursor = 0;
    this.replayLastStepId = null;
    this.replayBet = null;
    this.replayPeek = null;
  }

  // ── Win Classification ──

  computeWinType(winAmount, betAmount) {
    if (!winAmount || winAmount <= 0 || !betAmount || betAmount <= 0) return "";

    const multiplier = winAmount / betAmount;
    const thresholds = PROVIDER_CONFIG.WIN_THRESHOLDS;
    const winTypes = PROVIDER_CONFIG.WIN_TYPES;

    const sorted = Object.entries(thresholds).sort(([, a], [, b]) => b - a);
    for (const [thresholdKey, thresholdValue] of sorted) {
      if (multiplier >= thresholdValue) {
        const winTypeKey = thresholdKey.replace("_THRESHOLD", "");
        return winTypes[winTypeKey] || "regular_win";
      }
    }
    return "regular_win";
  }

  // ── Win Normalization ──

  // Match row positions against init line definitions to find line ID
  resolveLineId(positions) {
    if (!this.lines || !Array.isArray(positions)) return null;
    for (let i = 0; i < this.lines.length; i++) {
      const line = this.lines[i];
      if (!Array.isArray(line) || line.length !== positions.length) continue;
      let match = true;
      for (let j = 0; j < positions.length; j++) {
        if (line[j] !== positions[j]) {
          match = false;
          break;
        }
      }
      if (match) return i;
    }
    return null;
  }

  // Convert raw API wins to state format
  // API formats:
  //   Line win:  ["line", amount, [row0, row1, ...], lineId]
  //   Coins win: ["coins", payout, [[reel,row], ...]]     Mad Labs, 3 elements
  //   Coin win:  ["coin", value, reel, row]
  //   Bonus:     ["coins_game", totalWin]
  //   Jackpot:   ["jackpot", value]
  normalizeWins(rawWins, screen) {
    if (!Array.isArray(rawWins)) return [];
    return rawWins
      .map((winTuple) => {
        if (!Array.isArray(winTuple)) return null;

        // ── The Golden Mystery payout ──
        //
        // `["coins", payout, [[reel,row], ...]]`. THREE elements: no line id,
        // and the third is a list of coordinate pairs rather than a row per
        // reel. Verified on two live rounds — (1+2+2+5) x 10 = 100 and
        // (1+1) x 10 = 20, both exact — and `coords` is the feature's own cells.
        //
        // Until this branch existed the tuple fell through to the line-win
        // parser below, failed its `length < 4` check and was dropped: a Golden
        // Mystery paid nothing at all on a real round. The dev simulation hid
        // it, because a simulated round writes `wins` by hand.
        //
        // ── Deliberately NOT flagged isCoin ──
        //
        // In this engine `isCoin` means "the line that triggers Pot Collection
        // or Royal Collect": it suppresses the show-all phase, sorts the entry
        // last, draws no win box and waits for a feature screen to open. None
        // of that is true here. This is an ORDINARY win that happens to be paid
        // on cells instead of a line, so it is emitted as one — and the amount
        // is already final, level multiplier included.
        if (winTuple[0] === "coins") {
          const [, amount, positions] = winTuple;
          if (!Array.isArray(positions)) return null;
          return {
            id: null,
            win: amount || 0,
            positions: positions.filter((p) => Array.isArray(p)),
            // The coin. The cells themselves are still the masked mystery, and
            // the Golden Mystery owns their presentation — see the guards in
            // OverlayReelController — so nothing animates off this id. It is
            // here so the entry reads honestly.
            symbol: COIN_SYMBOL_IDS[0],
            // ── The feature pays ONCE, at the end, and shows nothing per cell ──
            //
            // This is how the reference game does it: the cells land, they all
            // spin out, and only when the last one has settled does the win
            // appear. No win box, no celebration on the coins — the coins ARE
            // the celebration, and they are already standing on the board with
            // their values printed on them.
            //
            // Its own flag rather than `isCoin`, which carries Pot Collection's
            // trigger semantics. This one means only: pay it, do not decorate
            // it.
            isFeaturePayout: true,
          };
        }

        // ── Scatter pay: ["scatter", amount, [[reel,row], ...]] ──
        //
        // Cleopatra's Fire. THREE elements and coordinate pairs, so it fell
        // through to the line parser below, failed `length < 4` and was
        // dropped — the scatter win never reached the game. It is real money
        // (three scatters pay 2x bet) AND the free-spins trigger entry, so it
        // is shaped like buildSyntheticScatterWin, with the server's amount.
        if (winTuple[0] === "scatter") {
          const [, amount, positions] = winTuple;
          if (!Array.isArray(positions)) return null;
          return {
            id: null,
            win: amount || 0,
            positions: positions.filter((p) => Array.isArray(p)),
            symbol: null,
            isScatter: true,
          };
        }

        // Coin win: individual coin payout
        if (winTuple[0] === "coin") {
          const [, value, reel, row] = winTuple;
          const rawSymbol = screen?.[reel]?.[row];
          let mappedSymbol = null;
          if (rawSymbol !== undefined) {
            mappedSymbol = PROVIDER_CONFIG.SYMBOL_MAP[rawSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[rawSymbol]
              : (typeof rawSymbol === "string" ? parseInt(rawSymbol, 10) : rawSymbol);
          }
          return {
            id: null,
            win: value || 0,
            positions: [[reel, row]],
            symbol: mappedSymbol,
            isCoin: true,
          };
        }

        // Bonus completion: total coins_game win
        if (winTuple[0] === "coins_game") {
          return {
            id: null,
            win: winTuple[1] || 0,
            positions: [],
            symbol: null,
            isBonus: true,
          };
        }

        // Grand jackpot
        if (winTuple[0] === "jackpot") {
          return {
            id: null,
            win: winTuple[1] || 0,
            positions: [],
            symbol: null,
            isJackpot: true,
            jackpotType: "grand",
          };
        }

        // Line win: ["line", amount, [rowPositions], lineId]
        if (winTuple.length < 4) return null;
        const [, amount, positions, lineId] = winTuple;
        if (!Array.isArray(positions)) return null;

        const transformedPositions = positions.map((rowValue, reelIndex) => {
          return [reelIndex, rowValue];
        });

        // Derive winning symbol — pick first non-wild from the screen
        let mappedSymbol = null;
        for (let p = 0; p < positions.length; p++) {
          const row = positions[p];
          const screenSymbol = screen?.[p]?.[row];
          if (screenSymbol !== undefined) {
            const mapped = PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol]
              : (typeof screenSymbol === "string" ? parseInt(screenSymbol, 10) : screenSymbol);
            if (mapped !== PROVIDER_CONFIG.WILD_SYMBOL_ID) {
              mappedSymbol = mapped;
              break;
            }
          }
        }
        // Fallback: all wilds — use first symbol
        if (mappedSymbol === null && positions.length > 0) {
          const row = positions[0];
          const screenSymbol = screen?.[0]?.[row];
          if (screenSymbol !== undefined) {
            mappedSymbol = PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol]
              : (typeof screenSymbol === "string" ? parseInt(screenSymbol, 10) : screenSymbol);
          }
        }

        return {
          id: lineId,
          win: amount,
          positions: transformedPositions,
          symbol: mappedSymbol,
        };
      })
      .filter((w) => w !== null);
  }

  // ── Adapt Round to State ──

  adaptSpinRoundToState(normalizedOutcome, bet) {
    if (!normalizedOutcome) return null;

    const totalWin = normalizedOutcome.win || 0;
    const winType = this.computeWinType(totalWin, bet);

    const round = {
      reels: normalizedOutcome.reels || [],
      wins: normalizedOutcome.wins || [],
      win: totalWin,
      bet,
      winType,
      isAnimatedWin: winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
      storage: normalizedOutcome.storage || null,
    };

    if (FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED) {
      round.anticipation = FEATURES_CONFIG.TENSION.ANTICIPATION.FORCE
        ? true
        : (normalizedOutcome.anticipation || false);
    }

    return round;
  }

  // ── Response Handler ──

  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: () => false,
            extractFreeSpins: () => [],
            extractTriggerRound: (round) => round,
          },
        ],
        regular: {
          condition: (response) =>
            response.flow?.state === "ready" ||
            response.flow?.state === "closed" ||
            response.flow?.state === "freespins",
          extractRound: (outcome) => {
            if (!outcome) return {};
            const transformedOutcome = { ...outcome };
            transformedOutcome.wins = Array.isArray(outcome.wins)
              ? this.normalizeWins(outcome.wins, outcome.screen)
              : [];
            return transformedOutcome;
          },
        },
      },
      propertyMap: {
        balance: "balance.wallet",
        bet: "outcome.bet",
        win: "balance.game",
        token: "flow.round_id",
        round: "outcome",
        reels: "outcome.screen",
        wins: "outcome.wins",
      },
    };
  }

  // ── Init Game ──

  async initGame(useApiData = false) {
    if (!window.__OPTIONS__) {
      throw new Error("PopiPlay: window.__OPTIONS__ not found.");
    }

    const apiUrl = window.__OPTIONS__?.api;
    const playToken = window.__OPTIONS__?.play_token;
    const csrfHeaderName = window.__OPTIONS__?.csrfTokenHeaderName;
    const csrfHeaderValue = window.__OPTIONS__?.csrfTokenHeaderValue;
    const identifier = window.__OPTIONS__?.identifier;

    const missingFields = [];
    if (!apiUrl) missingFields.push("api");
    if (!playToken) missingFields.push("play_token");
    if (!csrfHeaderName) missingFields.push("csrfTokenHeaderName");
    if (!csrfHeaderValue) missingFields.push("csrfTokenHeaderValue");
    if (!identifier) missingFields.push("identifier");

    if (missingFields.length > 0) {
      throw new Error(
        `PopiPlay: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`
      );
    }

    try {
      const response = await requestJson(apiUrl, {
        method: "POST",
        headers: {
          [csrfHeaderName]: csrfHeaderValue,
          "Content-Type": "application/json",
        },
        body: { command: "init" },
      });

      if (!response.data) {
        throw new Error("PopiPlay: Invalid API response - response.data is missing");
      }
      traceServerMoney("init", response.data);

      if (
        response.data.errors &&
        Array.isArray(response.data.errors) &&
        response.data.errors.length > 0
      ) {
        const error = response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      const availableBets = response.data?.options?.available_bets;
      const defaultBet = response.data?.options?.default_bet;
      const baseBet = response.data?.options?.base_bet;
      const paytable = response.data?.options?.paytable;
      const lines = response.data?.options?.lines;
      const wallet = response.data?.balance?.wallet;
      const currencyObj = response.data?.options?.currency;
      const jackpotMultipliers =
        response.data?.options?.jackpots ||
        response.data?.options?.feature_options?.jackpots ||
        null;

      const coinValues = response.data?.options?.coin_values || null;
      const progress = response.data?.progress || null;
      // Kept for the spins: play responses carry no options (jackpot coins).
      this.jackpots = jackpotMultipliers;
      // And the base bet, for the trigger coins' points -> money (coinValues).
      this.baseBet = baseBet;

      // Validate required fields
      if (!availableBets || !Array.isArray(availableBets)) {
        throw new Error("PopiPlay: Invalid API response - options.available_bets is missing or invalid");
      }
      if (typeof defaultBet !== "number") {
        throw new Error("PopiPlay: Invalid API response - options.default_bet is missing or invalid");
      }
      if (typeof baseBet !== "number") {
        throw new Error("PopiPlay: Invalid API response - options.base_bet is missing or invalid");
      }
      if (!paytable || typeof paytable !== "object") {
        throw new Error("PopiPlay: Invalid API response - options.paytable is missing or invalid");
      }
      if (!lines || !Array.isArray(lines)) {
        throw new Error("PopiPlay: Invalid API response - options.lines is missing or invalid");
      }
      this.lines = lines;
      if (typeof wallet !== "number") {
        throw new Error("PopiPlay: Invalid API response - balance.wallet is missing or invalid");
      }
      if (!currencyObj || typeof currencyObj.code !== "string") {
        throw new Error("PopiPlay: Invalid API response - options.currency.code is missing or invalid");
      }

      // Bonus buy prices, one multiplier per level. See parseBonusBuyMultipliers.
      //   currentBet * multiplier = cost in subunits
      //   EUR bet=100 (1.00) * 80 = 8000 subunits = 80.00 EUR
      const { levels: bonusBuyMultipliers, featureName: bonusBuyFeatureName } =
        parseBonusBuyMultipliers(response.data?.options?.feature_options);
      if (Object.keys(bonusBuyMultipliers).length === 0) {
        // Not fatal — the buy cards fall back to letting the server price the
        // purchase. Logged because it means the API moved the prices somewhere
        // parseBonusBuyMultipliers does not look yet.
        console.warn(
          "[PopiPlay] No bonus buy prices found. feature_options:",
          response.data?.options?.feature_options,
        );
      }
      // Every feature's levels (Hold & Win AND free spins) — the buy cards.
      const bonusBuyFeatures = parseBonusBuyFeatures(
        response.data?.options?.feature_options,
      );
      // Kept for the older two-tier consumers (primary UI).
      const bonusBuyMultiplier = bonusBuyMultipliers[1];
      const bonusBuyExtraMultiplier = bonusBuyMultipliers[2];

      const currencyConfig = {
        subunits: currencyObj.subunits || 100,
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2,
        symbol: currencyObj.symbol || currencyObj.code || "",
      };


      let featureState = this.detectFeatureState(response.data);
      // Hoisted above the round reconstruction below, which gates on it.
      // const freeSpinsState = this.detectFreeSpinsState(response.data);
      // `init`: a Hold & Win won inside free spins restores the pack too.
      const freeSpinsState = this.detectFreeSpinsState(response.data, { init: true });
      const storedState = this.loadGameState(response.data);

      // ── Completion Reload: bonus finished but localStorage not yet cleared ──
      // After bonus ends, the API returns a normal base game init (no bonus data).
      // But localStorage still has the full bonus state until the user presses spin.
      // Synthesize a completion featureState so the game replays the collection.
      // Skip when useApiData (error recovery): stale bonus LS on another tab must NOT
      // force type:"bonus" or getSpinParams keeps sending respin after dismiss.
      if (
        !useApiData &&
        featureState.type !== "bonus" &&
        StorageManager.hasBonusGameRound()
      ) {
        const savedRound = StorageManager.loadBonusGameRound();
        if (savedRound) {
          featureState = {
            type: "bonus",
            phase: "completion",
            data: {
              respinGameType: savedRound.respinGameType || "",
              currentRound: savedRound.round,
              initialRespinScreen: savedRound.round?.reels || savedRound.initialRespinScreen,
              initialRespinValues: savedRound.valuesGrid || savedRound.initialRespinValues,
              initialRespinOriginalValues: savedRound.originalValues || savedRound.initialRespinOriginalValues,
              valuesGrid: savedRound.valuesGrid,
              originalValues: savedRound.originalValues,
              prevScreen: savedRound.prevScreen,
              respinsLeft: savedRound.respinsLeft,
              respinsIssued: savedRound.respinsIssued,
              coinCount: savedRound.coinCount || 0,
              totalWin: savedRound.totalWin || 0,
              triggerCoordinates: savedRound.triggerCoordinates,
              initialEvents: savedRound.initialEvents || [],
              flippedCoinPositions: savedRound.flippedCoinPositions || [],
              screenProgress: [],
              isReload: true,
            },
          };
        }
      }

      // On reload during bonus, attach round data for grid restoration
      if (
        featureState.type === "bonus" &&
        (featureState.phase === "in_progress" || featureState.phase === "intro")
      ) {
        const outcome = response.data?.outcome;
        if (outcome?.screen && Array.isArray(outcome.screen)) {
          const normalizedScreen = normalizeScreenData(outcome.screen);
          featureState.data.currentRound = {
            reels: normalizedScreen,
            wins: outcome.wins || [],
            win: response.data?.balance?.game || 0,
            storage: outcome.storage || {},
          };
          if (featureState.phase === "in_progress") {
            featureState.data.isReload = true;
            // Ensure trigger_win key exists for WinDisplay on reload.
            // If not already set (from live play), bootstrap with balance.game.
            const existingTriggerWin = StorageManager.loadTriggerWin();
            if (!existingTriggerWin) {
              StorageManager.saveTriggerWin(response.data?.balance?.game || 0);
            }
          }
        }
      }

      // ── Reconstruct base game round from features.trigger_screen during bonus ──
      // Cold load: when LS missing. Error recovery (useApiData): always overwrite stale LS
      // with server trigger screen — extractRoundFromInit only has options.screen (wrong).
      // Bonus intro: same overwrite even without useApiData — stale LS from a previous
      // game on this browser (e.g. mobile→desktop) must not mix with the new intro.
      const forceBonusIntroFromApi =
        featureState.type === "bonus" && featureState.phase === "intro";

      if (forceBonusIntroFromApi) {
        const hadStaleLs =
          StorageManager.hasBonusGameRound() || StorageManager.hasBaseGameRound();
        if (hadStaleLs) {
          StorageManager.clearBonusGameRound();
          StorageManager.clearTriggerWin();
          StorageManager.clearBaseGameRound();
        }
      }

      // ── Free spins, gamble still open ──
      //
      // Same situation this block already handles for the respin bonus: the
      // player refreshed while a feature was owed something, and the base
      // board underneath has to be the TRIGGER board so the normal win
      // presentation has a round to run. Reusing the block rather than
      // writing a second one is the point — one reconstruction, one set of
      // helpers, one definition of what a trigger round looks like.
      //
      // Only while the ladder is still open — that is the only time the
      // payload can still carry the board (`outcome.screen` while the state
      // is gamble_freespins, and only until the first gamble answer).
      //
      // ── Gamble off: the intro reload is the server's ──
      //
      // Cleopatra's Fire. The real RGS answers an init during a live feature
      // with the PREVIOUS play response replayed (dmon-rgsim
      // session-restore-plan.md), so at 0 spins played the trigger spin's own
      // board and wins are right here — the server's copy, not a localStorage
      // record that may predate a fix. Mad Labs is unaffected: its intro init
      // has no board once the ladder closes, which is why it needs storage.
      const forceFreeSpinsFromApi =
        freeSpinsState?.data?.gambleSettled === false ||
        (!isGambleEnabled() &&
          freeSpinsState?.phase === "intro" &&
          Array.isArray(response.data?.outcome?.screen));

      // ── Completion: the round closed and the total was never shown ──
      //
      // Pot Collection's synthesized `phase: "completion"` (see the
      // hasBonusGameRound branch above), in the same shape and for the same
      // reason: the API has already returned to base, so the only record that
      // the outro is still owed is the stored one. The closing `freespin`
      // stamps it — see the fsFeatureLive branch in spin().
      //
      // Deliberately NO board and NO gamble data: nothing is re-presented,
      // only the total win dialog is owed. The room the player is standing in
      // is restored from the slice's own totals.
      const savedFreeSpinsRound = StorageManager.loadFreeSpinsRound();
      // ── THE RECORD decides, not the live state ──
      //
      // Pot Collection's rule, and the reason its completion has always worked:
      // it asks `hasBonusGameRound()` — does a record still exist — and never
      // consults what the API is reporting now. The record is written when the
      // round ends and cleared on the next BASE spin, so its existence IS the
      // outstanding debt, and clearing it is what says "done".
      //
      // This used to also require `!freeSpinsState`, which is the same thing
      // for a round the SERVER closed — its record is only ever stamped once
      // flow.state has left freespins, so the live state was already null and
      // the condition never did any work. For a CAPPED round it did: we close
      // it ourselves while the server still reports `freespins`, so the live
      // state was present and the completion was thrown away.
      //
      // `closed` alone, then. Only we stamp it, and only when the round is
      // genuinely over.
      const savedClosedFreeSpins = savedFreeSpinsRound;
      // ── ...except while the server says a Hold & Win runs INSIDE the pack ──
      // (2026-10-08) `state: respin` + freespins_* (detectFreeSpinsState,
      // init): the pack is live under the bonus, so a `closed` stamp on this
      // record came from the free spin that won it (spin() read its respin
      // state as the end, before fsFeatureLive learned it) — not an ending.
      // const holdAndWinRunsInsidePack =
      //   freeSpinsState?.data?.holdAndWinOwns === true &&
      //   response.data?.flow?.state === "respin";
      // Any state: after the settling respin it is `freespins` with the
      // bonus still in the payload (§8.4) — the pack is live just the same,
      // and the stale stamp opened the free spins' Total Win before the
      // bonus's collection (Vladimir, 2026-10-08: "showed up twice").
      const holdAndWinRunsInsidePack =
        freeSpinsState?.data?.holdAndWinOwns === true;
      const freeSpinsCompletion =
        // savedClosedFreeSpins?.closed === true
        savedClosedFreeSpins?.closed === true && !holdAndWinRunsInsidePack
          ? {
              phase: "completion",
              data: {
                gambleSettled: true,
                issued: savedClosedFreeSpins.awarded ?? 0,
                played: savedClosedFreeSpins.played ?? 0,
                totalWin: savedClosedFreeSpins.totalWin ?? 0,
                // The STAKE the round was played at, stamped on the record at
                // the trigger. The player may have changed the bet in the base
                // game before this reload; the owed outro and its "Nx BET"
                // must use the round's own stake (3 Orbs of Zeus 9f01aeb8,
                // 2026-10-08).
                bet: Number(savedClosedFreeSpins.bet) > 0
                  ? Number(savedClosedFreeSpins.bet)
                  : null,
                // The cap. Absent here, the slice defaulted to false and a
                // recovered MAX WIN round closed on the TOTAL WIN popup — the
                // one thing a capped round must never do.
                winLimitReached: storedWinLimitReached(savedClosedFreeSpins),
                // The board the round ended on, so the room is not blank
                // behind the outro — NORMALIZED, the same way a stored trigger
                // round is normalized further up this method.
                //
                // A record written before the schema carried the replay fields
                // has a board and wins but no triggerWin, no id and no
                // winType. The win controller returns at `!triggerWin`, the
                // loop then waits for a counter that only the presentation can
                // release, and the round hangs on a winning board. Derive them
                // rather than trust the record: a round that paid IS a
                // trigger round, and win/bet is all a win type ever needed.
                lastRound: savedClosedFreeSpins.round
                  ? (() => {
                      const r = savedClosedFreeSpins.round;
                      const roundWin = r.win || 0;
                      const roundBet = r.bet || savedClosedFreeSpins.bet || 0;
                      const winType =
                        r.winType || this.computeWinType(roundWin, roundBet);
                      return {
                        ...r,
                        bet: roundBet,
                        winType,
                        isAnimatedWin:
                          r.isAnimatedWin ??
                          (winType !== "" &&
                            PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType)),
                        triggerWin: r.triggerWin || roundWin > 0,
                        // Stable, and unique to this round: the win controller
                        // and the reveal both dedupe on it, and null reads as
                        // "already presented".
                        id:
                          r.id ??
                          `fs-outro-${savedClosedFreeSpins.triggerRound?.id ?? "x"}`,
                      };
                    })()
                  : null,
                fsState: null,
                level: savedClosedFreeSpins.level ?? 0,
                tier: savedClosedFreeSpins.tier ?? 0,
                serverLevel: savedClosedFreeSpins.serverLevel ?? 1,
              },
            }
          : null;

      fsDiag("init freeSpins decision", {
        apiPhase: freeSpinsState?.phase ?? null,
        flowState: response.data?.flow?.state ?? null,
        hasRecord: !!savedFreeSpinsRound,
        recordClosed: savedFreeSpinsRound?.closed ?? null,
        recordTotalWin: savedFreeSpinsRound?.totalWin ?? null,
        recordPlayed: savedFreeSpinsRound?.played ?? null,
        recordAwarded: savedFreeSpinsRound?.awarded ?? null,
        synthesizedCompletion: !!freeSpinsCompletion,
        // H&W inside the pack (2026-10-08): the pack's own reading of it.
        holdAndWinOwns: freeSpinsState?.data?.holdAndWinOwns ?? null,
        holdAndWinRunsInsidePack,
        freespinsLeft: response.data?.features?.freespins_left ?? null,
        bonusRespinsLeft: response.data?.features?.bonus_data?.respins_left ?? null,
        lastRoundTriggerWin: freeSpinsState?.data?.lastRound?.triggerWin ?? null,
      });

      // The record outlives the round on purpose — it owes the outro, and the
      // Total Win row reads it until the player spins the base game again.
      // Only a record with nothing left to show is dropped here.
      if (!freeSpinsState && !freeSpinsCompletion) {
        StorageManager.clearFreeSpinsRound();
      }

      let reconstructedBonusRound = null;
      if (
        (featureState.type === "bonus" &&
          (featureState.phase === "intro" || featureState.phase === "in_progress") &&
          (!StorageManager.hasBaseGameRound() || useApiData || forceBonusIntroFromApi)) ||
        forceFreeSpinsFromApi
      ) {
          const triggerScreen = response.data?.features?.trigger_screen || response.data?.outcome?.screen;
        if (triggerScreen && Array.isArray(triggerScreen)) {
          const normalizedReels = normalizeScreenData(triggerScreen);

          // Intro: full trigger round with coin wins (needed for entry animation).
          // In-progress: stripped — first respin already cleared wins; only reels matter.
          //
          // A free-spins gamble recovery is always the "intro" kind: the round
          // never started, so the trigger win is exactly what must replay.
          const isIntro = forceFreeSpinsFromApi || featureState.phase === "intro";

          // ── The SAME scan as the live and replay paths ──
          //
          // Literally the same function, not a board-only lookalike. This used
          // to call collectFreeSpinTriggerPositions, which only ever reads the
          // SCREEN — and a Golden Mystery's scatters are not on it, because a
          // B2 cell keeps symbol 10 whatever stands inside it.
          //
          // So a trigger the feature produced, or one split across the board and
          // the feature, found nothing here: no positions, no synthetic win, and
          // `gambleScatter` never built. On reload the cell had no package to
          // paint and came back as the empty window — a player who had just won
          // a level looking at a blank.
          //
          // A round-shaped shim because the scan reads both halves. `storage` is
          // where the feature's cells live; on an intro reload the API still
          // carries it, and when it does not the board half answers alone
          // exactly as it did before.
          const triggerRoundShim = {
            reels: normalizedReels,
            storage: response.data?.outcome?.storage || null,
          };
          const triggerPositions = isIntro
            ? collectAllTriggerPositions(triggerRoundShim)
            : [];
          // ── The REAL wins come back too ──
          //
          // This used to be the synthetic scatter and nothing else, so a
          // reload threw away every line the trigger spin paid. The stored
          // round then read `win: 2` with `wins: [scatter]` — a total with
          // nothing to show for it. useWinController saw a board whose only
          // entry was a trigger, took its trigger-only path, and skipped the
          // win presentation altogether: dimmed board, no lines, nothing
          // moving.
          //
          // Same shape as the live trigger path (see the isFreeSpinTrigger
          // block): the real wins first, the synthetic scatter appended last,
          // any pre-existing scatter dropped so it cannot be counted twice.
          // normalizeWins wants the RAW screen — it maps symbol ids itself.
          const restoredWins = isIntro
            ? this.normalizeWins(
                response.data?.outcome?.wins,
                triggerScreen,
              )
            : [];
          const syntheticWins = withTriggerScatterWin(
            restoredWins,
            triggerPositions,
          );

          const outcomeBet = response.data?.outcome?.bet || defaultBet;
          const outcomeWin = response.data?.outcome?.win || 0;
          const winType = isIntro ? this.computeWinType(outcomeWin, outcomeBet) : "";

          reconstructedBonusRound = {
            id: response.data?.flow?.round_id ?? `pc-${featureState.phase}`,
            reels: normalizedReels,
            wins: syntheticWins,
            win: outcomeWin,
            bet: outcomeBet,
            winType,
            isAnimatedWin: isIntro && winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
            triggerWin: isIntro, // Only true for intro — in_progress is already stripped
            // ── The trigger's OWN feature record rides with the board ──
            //
            // `trigger_screen` gives the symbols as they finally landed and
            // nothing else. The mystery coords and the Golden Mystery script
            // (mystery_feature, coins_feature) live in `storage`, and this
            // init's outcome.storage belongs to the CURRENT free spin, not to
            // the trigger. Without them the settled base board behind the
            // room had no script to paint its coins and magnets from, and
            // the room handed back twelve mystery lids where the free-spins
            // record's own arrival board showed the composites. Vladimir,
            // 2026-09-24: *"it should have same coins and elements inside as
            // it had when we came into the game."* The free-spins record
            // keeps the frozen trigger round, storage included — same source
            // the intro reload replays from.
            storage:
              savedFreeSpinsRound?.triggerRound?.storage ??
              triggerRoundShim.storage ??
              null,
          };

          // ── The package the reveal must land on ──
          //
          // Without this the trigger animates and SC1 opens onto nothing —
          // the same failure the replay path documents just above its own
          // call to this helper. The symbol plays its FULL sequence on every
          // reload, exactly as it did live — every intro reload is the same
          // show, only the package it lands on moves with the ladder.
          if (
            isGambleEnabled() &&
            forceFreeSpinsFromApi &&
            triggerPositions.length > 0
          ) {
            reconstructedBonusRound.gambleScatter = buildGambleScatterReveal(
              response.data?.features,
              response.data?.outcome,
              triggerPositions,
              // The feature's own award, exactly as the live path passes it.
              // Third in buildGambleScatterReveal's order of trust, so a board
              // round is unaffected — but without it a scatter the Golden
              // Mystery dropped rebuilt with no package at all, which is the
              // blank window this whole block exists to prevent.
              goldenGambleTriggerAward(triggerRoundShim),
            );
          }

          StorageManager.saveBaseGameRound({ round: reconstructedBonusRound, bet: outcomeBet });
        }
      }

      // ── Cleopatra's Fire Hold & Win: a reload ON the trigger replays it ──
      //
      // The init while `respin` is live is the previous play response, byte
      // for byte (contract §9). When that is the trigger itself (nothing
      // played yet — parseHoldAndWinState's restoreIntro), the entry runs
      // again exactly as live: the trigger spin's board, its real line wins,
      // then the coins' entry (withTriggerCoinWin) that opens the intro. The
      // positions come straight from storage.new_cells — the parser does not
      // replay deltas on a restore. Not for a random pot (coins not on reels).
      const holdAndWinInit = parseHoldAndWinState(response.data);
      // ── The bonus is ON SCREEN under this reload (2026-10-08) ── running or
      // settled, not on its intro: the free spins' "n LEFT" popup is not shown
      // at all, only the bonus's own respins popup (Vladimir: "we shouldnt
      // show this dialog at all"). On the intro the free-spins room replays
      // the trigger free spin, so its popup comes first there.
      if (freeSpinsState?.data && holdAndWinInit && !holdAndWinInit.restoreIntro) {
        freeSpinsState.data.holdAndWinOnScreen = true;
      }
      let holdAndWinTriggerRound = null;
      const hwScreen = response.data?.outcome?.screen;
      if (
        holdAndWinInit?.restoreIntro &&
        !holdAndWinInit.randomPot &&
        Array.isArray(hwScreen)
      ) {
        const hwBet = response.data?.outcome?.bet || defaultBet;
        const hwWin = response.data?.outcome?.win || 0;
        const hwWinType = this.computeWinType(hwWin, hwBet);
        // const coinPositions = (
        //   response.data?.outcome?.storage?.new_cells || []
        // ).map(([reel, row]) => [reel, row]);
        // On the reels (rows 4-7 of the grid are reel rows 0-3, 2026-10-14).
        const coinPositions = (
          triggerCellsOnReels(response.data?.outcome?.storage?.new_cells) || []
        ).map(([reel, row]) => [reel, row]);
        holdAndWinTriggerRound = {
          id: response.data?.flow?.round_id ?? null,
          reels: normalizeScreenData(hwScreen),
          wins: withTriggerCoinWin(
            this.normalizeWins(response.data?.outcome?.wins, hwScreen),
            // coinPositions,
            // The server's cells, else the coins on the board (symbols).
            coinPositions.length
              ? coinPositions
              : scanBoardCoins(normalizeScreenData(hwScreen)),
          ),
          win: hwWin,
          bet: hwBet,
          winType: hwWinType,
          isAnimatedWin:
            hwWinType !== "" &&
            PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(hwWinType),
          triggerWin: true,
          storage: response.data?.outcome?.storage || null,
        };
        markJackpotCoins(
          holdAndWinTriggerRound,
          // response.data?.outcome?.storage?.new_cells,
          triggerCellsOnReels(response.data?.outcome?.storage?.new_cells),
          jackpotMultipliers,
        );
        // The trigger coins' own values on the reloaded trigger screen.
        assignCoinValues(
          holdAndWinTriggerRound,
          // response.data?.outcome?.storage?.new_cells,
          triggerCellsOnReels(response.data?.outcome?.storage?.new_cells),
          hwBet,
          baseBet,
        );
        // StorageManager.saveBaseGameRound({
        //   round: holdAndWinTriggerRound,
        //   bet: hwBet,
        // });
        // ── Won on a FREE spin: it replays in the free-spins room ──
        // (2026-10-08) its board, its wins and the coins' entry, presented by
        // the free-spins win controller, which opens the intro from it as it
        // does live. The base game keeps its own round.
        if (freeSpinsState?.data) {
          freeSpinsState.data.lastRound = holdAndWinTriggerRound;
          fsDiag("init H&W intro inside free spins", {
            fsPhase: freeSpinsState.phase,
            played: freeSpinsState.data.played,
            left: freeSpinsState.data.left,
            totalWin: freeSpinsState.data.totalWin,
            triggerWin: holdAndWinTriggerRound.triggerWin,
            win: holdAndWinTriggerRound.win,
            wins: holdAndWinTriggerRound.wins?.length ?? 0,
          });
        } else {
          StorageManager.saveBaseGameRound({
            round: holdAndWinTriggerRound,
            bet: hwBet,
          });
        }
      }

      // ── The Random Pot, reloaded on its trigger (GDD: "Skipping or
      // reloading must preserve the awarded Coin positions, values and bonus
      // state without repeating the award") ── the paid spin comes back as it
      // stopped — its own board and its own wins, no coin line (the pot's
      // coins are not on the reels) — and the presentation spin's source is
      // kept exactly as spin() keeps it live (_randomPotTrigger), from this
      // same replayed trigger response. Nothing is re-sent: the presentation
      // spin reads it, lands the awarded coins, and the normal intro follows.
      if (
        holdAndWinInit?.restoreIntro &&
        holdAndWinInit.randomPot &&
        Array.isArray(hwScreen)
      ) {
        const potBet = response.data?.outcome?.bet || defaultBet;
        const potWin = response.data?.outcome?.win || 0;
        const potWinType = this.computeWinType(potWin, potBet);
        holdAndWinTriggerRound = {
          id: response.data?.flow?.round_id ?? null,
          reels: normalizeScreenData(hwScreen),
          wins: this.normalizeWins(response.data?.outcome?.wins, hwScreen),
          win: potWin,
          bet: potBet,
          winType: potWinType,
          isAnimatedWin:
            potWinType !== "" &&
            PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(potWinType),
          // Its wins present first (GDD "Pot activation": complete the current
          // Base Game result and any awarded wins), then the Pot.
          triggerWin: potWin > 0,
          storage: response.data?.outcome?.storage || null,
        };
        StorageManager.saveBaseGameRound({
          round: holdAndWinTriggerRound,
          bet: potBet,
        });
        this._randomPotTrigger = {
          // The shape spin() resolves with — what the presentation spin hands
          // to rgsPlay. holdAndWin re-parsed as the TRIGGER it replays.
          result: {
            token: response.data?.flow?.round_id || null,
            bet: potBet,
            balance: (wallet ?? 0) + potWin,
            round: holdAndWinTriggerRound,
            gameMode: "base",
            featureState: { type: null, phase: null, data: null },
            availableActions: response.data?.flow?.available_actions || [
              "init",
              "respin",
            ],
            flowState: response.data?.flow?.state || "respin",
            features: response.data?.features || null,
            holdAndWin: parseHoldAndWinState({
              ...response.data,
              flow: { ...(response.data?.flow || {}), command: "spin" },
            }),
            command: "spin",
          },
          // rawNewCells: response.data?.outcome?.storage?.new_cells || [],
          rawNewCells: triggerCellsOnReels(response.data?.outcome?.storage?.new_cells) || [],
          bet: potBet,
        };
      }

      const balance = wallet;
      const savedBaseGame = StorageManager.loadBaseGameRound();

      // ── A LOST gamble leaves a trigger round nothing will ever consume ──
      //
      // The player bought the bonus, gambled it and lost. The round is OVER,
      // and the server says so three times over: `flow.state: "ready"`,
      // `available_actions: ["init", "spin"]`, and no `features` block at all.
      // Per the contract table (docs/api-contract-mad-labs-freespins.md), a
      // feature that still owes the player something always offers an action
      // for it — `gamble_freespins`/`collect_freespins` while the ladder is
      // open, `freespin` once the package is taken. None of those is here.
      //
      // detectFreeSpinsState reads that correctly and returns null, and the
      // free-spins record is cleared further up. The BASE record is not — and
      // that is the one the round is restored from. It still holds the trigger
      // board, its synthetic scatter win, `triggerReveal` and the whole
      // `gambleScatter` reveal payload, so every reload re-opened the doors,
      // span the strip, and then stopped dead. There is no feature left to
      // enter, and nothing on the client can tell that from the record alone.
      //
      // ── Why the AVAILABLE ACTIONS and not the state ──
      //
      // `state: "ready"` and `state: "closed"` both mean "back to base" and
      // both offer only `spin`, so keying on one state name would miss the
      // other. The actions are the server's own statement of what may still
      // happen, and they are already what every send site guards on.
      //
      // ── What is kept: the BOARD, and nothing else ──
      //
      // The reload lands on a finished round. Every win it ever paid has been
      // paid and banked — `balance.game` is 0 and the wallet already holds it —
      // so there is nothing left to present. The board is the last thing the
      // player saw and it should still be there, as TEXTURES: no reveal, no
      // scatter win, no line cycle, no counter.
      //
      // So the record is reduced rather than edited. Every field the win
      // presentation reads is cleared at once, because leaving any one of them
      // is enough to start a sequence: `triggerWin` is the gate
      // useWinController returns at, `triggerReveal` launches the doors,
      // `gambleScatter` is what the strip lands on, and `wins` is what the
      // cycle walks.
      const pendingFeatureActions = ["gamble_freespins", "collect_freespins", "freespin"];
      const featureStillOwed =
        !!freeSpinsState ||
        !!freeSpinsCompletion ||
        (response.data?.flow?.available_actions || []).some((action) =>
          pendingFeatureActions.includes(action),
        );

      const bet = storedState.bet || savedBaseGame?.bet || defaultBet;
      // useApiData / forced bonus intro reconstruct → prefer trigger round (never stale LS).
      // useApiData + base/FR → extractRoundFromInit (options.screen) as before.
      // ── Intro phase: the board is (usually) gone from the payload ──
      //
      // `outcome.screen` is null once the state is `freespins` — and ALSO the
      // moment the ladder is climbed once: the FIRST gamble answer erases the
      // whole outcome while the state is still `gamble_freespins` (verified
      // live). So for the whole intro phase the trigger round comes from
      // where the trigger response put it, and the entry sequence replays
      // identically on every reload until the phase advances — the same
      // thing Pot Collection's intro reload does, and the same reason it
      // needs storage to do it. The reveal's package stays current because
      // every gamble answer patches the stored gambleScatter in place — see
      // _patchStoredGambleData.
      // The frozen entry board, out of the one record — Pot Collection reads
      // initialRespinScreen from its bonus record in exactly this way.
      const savedFreeSpinsTrigger =
        freeSpinsState?.phase === "intro" && savedFreeSpinsRound?.triggerRound
          ? {
              round: savedFreeSpinsRound.triggerRound,
              bet: savedFreeSpinsRound.bet,
            }
          : null;

      // A stored trigger round IS a trigger round — normalize the flag on
      // load. Rounds saved before the flag was stamped at save time would
      // otherwise replay nothing and drop the reload straight onto the
      // gamble screen with no win sequence.
      if (savedFreeSpinsTrigger?.round && !savedFreeSpinsTrigger.round.triggerWin) {
        savedFreeSpinsTrigger.round = {
          ...savedFreeSpinsTrigger.round,
          triggerWin: true,
        };
      }

      let round =
        // holdAndWinTriggerRound ||
        // Not the base round when it was won on a free spin — see above.
        (freeSpinsState ? null : holdAndWinTriggerRound) ||
        (!isGambleEnabled() && forceFreeSpinsFromApi && reconstructedBonusRound) ||
        savedFreeSpinsTrigger?.round ||
        (useApiData || forceBonusIntroFromApi || forceFreeSpinsFromApi
          ? (reconstructedBonusRound || this.extractRoundFromInit(response.data))
          : storedState.round || savedBaseGame?.round || this.extractRoundFromInit(response.data));

      // ── A LOST gamble: come back to the board the player was left on ──
      //
      // Closing the gamble on a loss strips the board live — no wins, no
      // reveal, just textures. A reload has to land on THAT, and it did not:
      // it replayed the whole entry sequence and then stopped, because the
      // stored round still carried the trigger.
      //
      // ── Applied HERE, to the round that was chosen ──
      //
      // and not to any one source, because the chain above has five of them
      // and two read the SAME storage key by different routes —
      // `storedState.round` (loadGameState, called at the top of this method)
      // and `savedBaseGame.round`. Silencing one leaves the other, and
      // `storedState` wins the `||`. One gate after the choice cannot be
      // half-applied.
      //
      // ── Only when the server says nothing is owed ──
      //
      // Per the contract table, a feature that still owes the player something
      // always offers an action for it: `gamble_freespins`/`collect_freespins`
      // while the ladder is open, `freespin` once the package is taken. A lost
      // gamble offers only `spin`. So an intro reload — which MUST replay the
      // sequence — is untouched, and so is an ordinary base board.
      //
      // Reduced, not edited. Every field the TRIGGER's presentation reads goes
      // at once, because any one of them left behind restarts it: `triggerWin`
      // is the gate useWinController returns at, `triggerReveal` opens the
      // doors, `gambleScatter` is what the strip lands on, and the scatter
      // entry in `wins` is the line that dims the board for it.
      //
      // ── But the FEATURE's payout is not part of that show ──
      //
      // This used to empty `wins` and set `win: 0` outright, on the argument
      // that "the round is finished and everything it paid is already banked".
      // The banking is true and the presentation is not: a Golden Mystery pays
      // through the same `wins` array, as one `["coins", payout, cells]` entry,
      // and that entry has nothing to do with the gamble.
      //
      // Round 1472920 is the case. The spin paid 9510 — `balance.game 9510`,
      // `outcome.win 9510`, `wins: [["coins", 9510, [...22 cells]]]` — and its
      // board also carried a BLANK gamble scatter, which put an `isScatter`
      // entry in `wins` and brought the whole round through here. So a reload
      // replayed the entire feature, coin by coin, over a board worth 9510, and
      // then paid nothing at all: no counter, no dim, no win text. The history
      // showed the win; the game did not. Vladimir, 2026-09-22.
      //
      // So the feature payout is KEPT and the round's win is rebuilt from what
      // survives.
      //
      // ── And so is every OTHER money win: only the scatter goes ──
      //
      // Line wins used to go with the trigger, on the same argument, and a
      // lost gamble with a paying line came back with the line silent.
      // Vladimir, 2026-09-24: *"no wins playing even if we had a win, we must
      // differentiate scatters from other symbols."* The scatter entry is the
      // trigger's own line — it opens the doors — and it is the only thing the
      // lost package takes with it. What the round paid replays like any other
      // reloaded win, the money withheld and paid back by that replay; live,
      // the same round cycles those lines once the dialog closes (see
      // gambleLostForRoundRef in useWinController).
      //
      // ── A BLANK gamble scatter is not a trigger ──
      //
      // It awarded nothing, so there is no entry sequence to protect: the doors
      // open onto an empty window and the round goes on as an ordinary one. So
      // a finished round with a blank is NOT reduced — it replays in full, the
      // reveal, the Golden Mystery and the win, and the money is withheld and
      // paid back by the replay (initializeGame.fulfilled for the balance,
      // freeRoundsSlice for the free-rounds total). Vladimir, 2026-09-24, on a
      // free-rounds reload of a blank that came back with nothing: *"we have to
      // play this round as brand new."*
      //
      // Only the blank. For one day this whole block was deleted for that
      // case, and a LOST gamble replayed its scatter sequence again. Same test
      // as the win cycle's — see linePackageBlank in useWinController.
      const reloadedPackage = round?.gambleScatter;
      const blankScatter =
        !!reloadedPackage &&
        (reloadedPackage.value ?? 0) <= 0 &&
        (reloadedPackage.tier ?? 0) <= 0;
      if (
        !featureStillOwed &&
        !blankScatter &&
        round &&
        (round.triggerReveal === true ||
          !!round.gambleScatter ||
          (round.wins || []).some((w) => w?.isScatter))
      ) {
        const keptWins = (round.wins || []).filter((w) => !w?.isScatter);
        const keptWin = keptWins.reduce((total, w) => total + (w?.win || 0), 0);
        round = {
          ...round,
          wins: keptWins,
          win: keptWin,
          // Both describe the celebration TIER, and the tier was computed from
          // the win that is being rebuilt. Kept while money remains, cleared
          // with it — never a tier for an amount that is no longer there.
          winType: keptWin > 0 ? round.winType || "" : "",
          isAnimatedWin: keptWin > 0 ? !!round.isAnimatedWin : false,
          gambleScatter: null,
          // ── ...but the PICTURE it left on the board survives ──
          //
          // The cell the player comes back to is an OPEN gold window with the
          // package inside it. Rebuilt from its symbol id alone it is a CLOSED
          // door, which is what a reload has always shown — Vladimir reported it
          // 2026-09-11 and it has outlived two attempts at a fix.
          //
          // A SEPARATE FIELD, deliberately. `gambleScatter` stays null because a
          // dozen consumers branch on it — the reveal, the win cycle's blank
          // test, the skip package, the gamble dialog — and any one of them
          // waking up is the lost-gamble replay this whole block exists to stop.
          // This one is read in exactly one place, Reel.jsx's
          // paintSettledReveal, and means only "draw the window like this".
          //
          // It is also its own guard: nothing but this branch ever writes it, and
          // this branch has already proved the server owes no feature
          // (`featureStillOwed`). So a LIVE trigger cannot carry it, and the
          // award can never be painted before the doors open.
          settledGambleScatter: round.gambleScatter || null,
          triggerReveal: false,
          // The gate useWinController returns at. The trigger half is gone
          // either way; what is left is an ordinary win, presented the way any
          // other reloaded win is.
          triggerWin: keptWin > 0,
        };
      }

      // ── A BLANK replays in full, so it gets its reveal flag back ──
      //
      // `triggerReveal` is derived (hasRevealSequence) and saveBaseGameRound
      // does not keep it, so a blank restored from storage came back false.
      // The win cycle then took it for an ordinary win: it dimmed the board at
      // once, before the doors, and the blank's ending — which expects no dim
      // — never took it down. A blank carries `gambleScatter`, and that
      // payload exists only when SC1 is on screen, which is the live rule's
      // own test. Vladimir, 2026-09-25: *"when we have blank the board should
      // clear properly."*
      if (blankScatter && !featureStillOwed && round) {
        round = { ...round, triggerReveal: true };
      }

      // ── The PICTURE travels even when the strip does not ──
      //
      // The block above is gated on `!featureStillOwed`, and rightly: a round
      // whose feature still has something to present must not be reduced. But
      // `featureStillOwed` includes `freeSpinsCompletion` — the outro a capped
      // or finished round is still owed — and that is the single most common
      // way a player comes back to a settled board. So on exactly that reload
      // the whole block was skipped and `settledGambleScatter` was never
      // written; the base reel rebuilt the cell from symbol 12 and the player
      // got the CLOSED gold door. Measured 2026-09-23:
      // `paintSettledReveal — controller=true settled=null roundId=null`.
      //
      // Carrying the picture is safe wherever the strip is not, because it is
      // only ever READ by Reel.jsx's paintSettledReveal. It changes no state
      // machine, arms no dialog and gates nothing.
      //
      // ── EXCEPT where the reveal has still to be watched ──
      //
      // An intro reload replays the whole scatter sequence, and a gamble whose
      // ladder is still open has not been answered yet. Painting the award on
      // either would show it before the doors open. `forceFreeSpinsFromApi` IS
      // the "ladder still open" test (`gambleSettled === false`), and the two
      // intro flags are the replay ones — the same three the earlier notes on
      // this bug said to exclude.
      const revealStillOwed =
        forceFreeSpinsFromApi ||
        forceBonusIntroFromApi ||
        freeSpinsState?.phase === "intro" ||
        featureState?.phase === "intro";
      // Only when the base board is NOT about to replay the round: a finished
      // round with a BLANK scatter is not stripped (see above), so it replays
      // its reveal on reload, and that reveal paints the settled window at its
      // own end. Painting it here too would show the answer before the doors
      // open — and the uncover effect in useWinController would take the
      // reveal's spine off mid-spin. So this is for a round whose feature is
      // still owed (the room, or its completion), where the base board comes
      // back without replaying.
      //
      // ── And the whole base round is a FINISHED board here ──
      //
      // This is the case where the room is restored straight away — mid-round,
      // or at its completion — so the base round is the trigger spin, played
      // and presented BEFORE the bonus. When the room hands the screen back,
      // that round must come back as its finished picture: no mystery reveal,
      // no win. It used to replay both, once the base reveal stopped getting
      // stuck on the frozen controller list. Vladimir, 2026-09-24: *"when we
      // come back to base game, we dont have to replay it... that round is
      // actually done."*
      //
      // A FLAG, and deliberately not a reduction of the round: the room's
      // arrival board is built from this same round (freeSpinsSlice,
      // arrivalBoardFrom) and still needs its mystery_feature and gamble
      // scatter. Only the base game's consumers read it — the mystery reveal,
      // the win presentation, the reel paint and the reload withholding.
      if (featureStillOwed && !revealStillOwed && round) {
        // ── The Golden Mystery's script travels the same way the card does ──
        //
        // `settledGambleScatter` is how the scatter's picture survives every
        // reload: carried ON the round, whatever the stored board holds. The
        // golden cells had no such carrier. Their coins and magnets are
        // painted from `storage` (mystery_feature, coins_feature), and the
        // stored base round can come back without it — rebuilt from the
        // server's trigger_screen on one reload, then saved that way for the
        // next — so the settled board behind the room came back as twelve
        // mystery lids while the scatter cell showed its card. Vladimir,
        // 2026-09-24: *"just do what the scatter is doing."* The free-spins
        // record keeps the frozen trigger round with its storage for exactly
        // this; a round that already carries its script keeps its own.
        const ownStorage = round.storage;
        const hasScript =
          !!ownStorage?.coins_feature || !!ownStorage?.mystery_feature;
        const triggerStorage = savedFreeSpinsRound?.triggerRound?.storage;
        round = {
          ...round,
          boardSettled: true,
          settledGambleScatter:
            round.settledGambleScatter || round.gambleScatter || null,
          storage: hasScript ? ownStorage : triggerStorage ?? ownStorage ?? null,
        };
      }

      // ── A board, always, whenever the feature is in play ──
      //
      // The chain above can settle on a record that has no reels: storage
      // cleared by hand, a stripped base round, or the intro phase with
      // nothing saved because the round predates the trigger being persisted.
      // The reels then render empty, and the player watches an intro and a
      // wipe over a blank machine.
      //
      // `options.screen` is in EVERY init — it is the idle board the server
      // offers for display, regenerated each call. It is not the trigger
      // board and it never will be, so nothing is animated from it: no wins,
      // no triggerWin, no gambleScatter. It is only there so the base game
      // looks like a slot while the entry sequence plays over it.
      //
      // Last resort by design. Every better source is tried first, and this
      // only fills a hole that would otherwise be a blank screen.
      const freeSpinsRound =
        freeSpinsState && !round?.reels?.length
          ? this.extractRoundFromInit(response.data)
          : null;
      if (freeSpinsRound) {
        console.warn(
          "[FreeSpins] Reload: no stored board for phase",
          freeSpinsState.phase,
          "- falling back to the init's idle screen so the reels are not empty.",
        );
      }
      const resolvedRound = freeSpinsRound || round;
      // ── The board's coins get their numbers ONCE, here ──
      //
      // A spin's round is given its coinValues in the adapter, but the init's
      // own board (extractRoundFromInit — options.screen) never was. With no
      // value on the round the reel's texture and the overlay's idle spine each
      // drew their own random number, so the coins on screen changed value the
      // moment the first spin swapped the idles for their textures. A stored
      // round already carries its values and is left alone.
      if (resolvedRound?.reels && !resolvedRound.coinValues) {
        assignCoinValues(
          resolvedRound,
          null,
          response.data?.free_rounds?.bet_value || bet,
          baseBet,
        );
      }
      const gameMode = this.getGameMode(featureState, freeSpinsState);

      // ── Free Rounds detection ──
      // PopiPlay sends free rounds as a top-level `free_rounds` object
      // and cumulative win in `balance.free_rounds`
      const freeRoundsData = response.data?.free_rounds || null;
      const freeRoundsTotalWin = response.data?.balance?.free_rounds ?? 0;

      // ── An owed feature is shown at the stake it is PLAYED at ──
      //
      // `bet` above is the player's own last choice (the stored base round).
      // While a free-spins round or a Hold & Win is still owed — intro,
      // mid-round or the completion outro — the toolbar and the outro's
      // "Nx BET" must read the round's stake, not a bet the player picked in
      // the base game before the reload (3 Orbs of Zeus 9f01aeb8, 2026-10-08).
      // The player's own bet travels as featureBetToRestore and is put back
      // when that feature hands over to the base game — gameSettingsSlice,
      // resetFreeSpins / resetHoldAndWin. Free spins first: a Hold & Win won
      // inside them is played at their stake and hands back to them.
      const freeSpinsStake =
        (freeSpinsState || freeSpinsCompletion) &&
        Number(savedFreeSpinsRound?.bet) > 0
          ? Number(savedFreeSpinsRound.bet)
          : null;
      // Hold & Win: live on the server (its init IS the last play response,
      // outcome.bet the stake), or settled and replayed from the record the
      // settling respin stamped with its stake.
      const holdAndWinStake = (() => {
        if (holdAndWinInit) {
          const liveStake = Number(response.data?.outcome?.bet);
          return liveStake > 0 ? liveStake : null;
        }
        if (useApiData) return null;
        const settledStake = Number(StorageManager.loadHoldAndWinSettled()?.bet);
        return settledStake > 0 ? settledStake : null;
      })();
      const featureStake = freeSpinsStake ?? holdAndWinStake;
      const featureBetOwner =
        freeSpinsStake !== null
          ? "freeSpins"
          : holdAndWinStake !== null
            ? "holdAndWin"
            : null;

      const result = {
        balance,
        // bet,
        bet: featureStake ?? bet,
        featureBetToRestore:
          featureStake !== null && featureStake !== bet ? bet : null,
        // Which feature's hand-back puts it back.
        featureBetOwner,
        baseBet,
        bets: availableBets,
        round: resolvedRound,
        token: playToken,
        currency: currencyObj.code,
        currencyConfig,
        paytable: Object.fromEntries(
          Object.entries(paytable).map(([apiId, values]) => [
            PROVIDER_CONFIG.SYMBOL_MAP[apiId] ?? apiId,
            values,
          ])
        ),
        winningLines: lines,
        bonusBuyMultiplier,
        bonusBuyExtraMultiplier,
        bonusBuyMultipliers,
        bonusBuyFeatureName,
        bonusBuyFeatures,
        game: identifier,
        gameMode,
        featureState,
        reloadState: storedState.reloadState,
        availableActions: response.data?.flow?.available_actions || ["init", "spin"],
        flowState: response.data?.flow?.state || "ready",
        features: response.data?.features || null,
        // Cleopatra's Hold & Win, parsed from features.bonus_data — see
        // holdAndWinState.js. Null when the round has no H&W.
        // holdAndWin: parseHoldAndWinState(response.data),
        // ── …or the SETTLED round still owed on screen ── once the last
        // respin has settled, the server's init is a plain base init (no
        // bonus_data), so a reload before the collection finished found
        // nothing and dropped it (Vladimir, 2026-09-30: "reload on the last
        // round … should replay the collect, and then show us the dialog").
        // The settling step was stored (spin(), below) — replayed from it:
        // the room restores on the final grid and the settled path runs the
        // scarab reveal, the End Collector and the Total Win. Deltas are not
        // replayed (they are already on the grid). Not on error recovery.
        holdAndWin:
          parseHoldAndWinState(response.data) ??
          (() => {
            if (useApiData) return null;
            const settled = StorageManager.loadHoldAndWinSettled()?.holdAndWin;
            if (!settled) return null;
            return {
              ...settled,
              phase: "settled",
              restoreIntro: false,
              restoreSettled: true,
              newCells: [],
              updatedCells: [],
            };
          })(),
        // ── Reload recovery for the free-spins feature ──
        // The same shape spin responses already carry, so freeSpinsSlice can
        // read the ladder from an init exactly as it reads it from a gamble.
        gamble: parseGambleState(response.data?.features),
        // Null unless the session is genuinely mid-feature. Mirrors how
        // `featureState` carries Pot Collection's phase off the same call.
        // A closed round that still owes its total win reports the synthesized
        // completion phase, exactly as Pot Collection's featureState does.
        // COMPLETION WINS. It is only ever built from a record stamped
        // `closed`, which means the round is over — and on a capped round the
        // live state is still mid-feature, so the old order (`freeSpinsState ||
        // …`) handed back "in progress" for a round that had already ended.
        freeSpinsState: freeSpinsCompletion || freeSpinsState,
        freeRounds: freeRoundsData,
        freeRoundsTotalWin,
        command: "init",
        jackpotMultipliers,
        coinValues,
        progress,
      };

      // Persist base game on first load
      if (
        result.gameMode === "base" &&
        result.round &&
        (!StorageManager.hasBaseGameRound() || useApiData)
      ) {
        StorageManager.saveBaseGameRound({ round: result.round, bet: result.bet, balance: result.balance });
      }

      // Persist free rounds state on init (for reload recovery)
      if (freeRoundsData?.status === "active") {
        if (StorageManager.hasFreeRoundsRound()) {
          // Exists → update changing fields, never touch betToRestore
          StorageManager.saveFreeRoundsRound({
            status: freeRoundsData.status,
            betValue: freeRoundsData.bet_value,
            performed: freeRoundsData.performed,
            count: freeRoundsData.count,
            win: freeRoundsTotalWin,
          });
        } else {
          // Doesn't exist → create with betToRestore from base_game_round
          // savedBaseGame was loaded BEFORE base_game_round gets overwritten (line 568)
          StorageManager.saveFreeRoundsRound({
            status: freeRoundsData.status,
            betValue: freeRoundsData.bet_value,
            performed: freeRoundsData.performed,
            count: freeRoundsData.count,
            win: freeRoundsTotalWin,
            betToRestore: savedBaseGame?.bet ?? result.bet,
          });
        }

        // ── FR round 0: reset base game to fresh neutral state ──
        // No rounds played yet — use fresh API reels with no wins.
        // Prevents stale base game win from showing on reload.
        if (freeRoundsData.performed === 0) {
          // Clear stale bonus game data — if FR was assigned during a bonus,
          // the bonus is finished but localStorage wasn't cleared (no base spin
          // happened between bonus end and FR activation).
          StorageManager.clearBonusGameRound();

          const freshRound = this.extractRoundFromInit(response.data);
          if (freshRound) {
            // Its coins' numbers, as for the board above.
            assignCoinValues(
              freshRound,
              null,
              freeRoundsData.bet_value || result.bet,
              baseBet,
            );
            StorageManager.saveBaseGameRound({
              round: freshRound,
              bet: savedBaseGame?.bet ?? result.bet,
              balance: result.balance,
            });
            // Also update the result so the current session uses fresh reels
            result.round = freshRound;
          }
        }
      }

      // ── FR canceled externally (operator revoked during bonus or base game) ──
      // Throw 206 immediately to trigger error recovery overlay.
      // This resets the entire game state and pushes the user to a clean base game.
      if (freeRoundsData?.status === "canceled") {
        StorageManager.clearFreeRoundsRound();
        StorageManager.clearBonusGameRound();
        const cancelError = {
          errors: [{ code: 206, desc: "free_rounds_canceled" }],
        };
        throw cancelError;
      }

      // ── FR Completion Reload: FR finished but localStorage not yet cleared ──
      // After FR ends, the API returns no free_rounds (base game init).
      // But localStorage still has FR data until the user plays a base game spin.
      // Synthesize a "played" status so the slice sets completionPending
      // and the hook replays the completion popups.
      // EXCEPTION: If performed < count, the FR was canceled externally (operator),
      // not completed — clear stale localStorage instead of showing completion.
      if (
        !freeRoundsData?.status &&
        StorageManager.hasFreeRoundsRound()
      ) {
        const savedFR = StorageManager.loadFreeRoundsRound();
        if (savedFR) {
          const wasCompleted = savedFR.performed >= savedFR.count;
          if (wasCompleted) {
            // Legitimate completion — synthesize "played" for completion popups
            result.freeRounds = {
              status: "played",
              bet_value: savedFR.betValue,
              performed: savedFR.performed,
              count: savedFR.count,
              // Tells the slice whether `win` below already includes the last
              // round's own win (reveal played before reload) or not (reload
              // interrupted the reveal) — decides if withholding is valid.
              lastRoundApplied: savedFR.lastRoundApplied ?? true,
            };
            result.freeRoundsTotalWin = savedFR.win ?? 0;
          } else {
            // External cancellation — clear stale localStorage, start fresh
            StorageManager.clearFreeRoundsRound();
          }
        }
      }

      return result;
    } catch (error) {
      // Structured errors (from API or synthesized 206) — pass through as-is
      if (error?.errors && Array.isArray(error.errors)) {
        throw error;
      }
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Init API call failed: ${error.message}`);
    }
  }

  // ── Replay Session (docs/replay-history-spec.md §0) ──
  //
  // A pure on/off switch on this same object, deliberately NOT a
  // parallel adapter — every other feature here (Hold&Win, Free Spins, Pot
  // Collection, Bonus Buy) already shares this one initGame()/spin() pair,
  // told apart by state/params rather than a swapped adapter, so replay
  // follows the same shape and the existing bonus/free-spin detection logic
  // (detectFeatureState) keeps working with zero new code.
  //
  // initGame() itself is untouched (Stage 1 uses a separate lightweight
  // fetchReplayInit() instead, see below). spin() and getBalanceStrategy()
  // both branch on this.replayMode near the top of their existing bodies —
  // neither's live-mode logic below was otherwise changed.

  /**
   * Switch this object into replay mode for the given round's replay API
   * base URL (the `replayUrl` extracted from a history row's `replay_link`,
   * see _extractReplayUrl below).
   * @param {string} replayUrl
   */
  enterReplayMode(replayUrl) {
    this.replayMode = true;
    this.replayUrl = replayUrl;
    // A peek belongs to one playthrough of one round. Replay Again re-enters
    // here, and a peek held from the previous pass would answer for the wrong
    // step.
    this.replayPeek = null;
    // A Random Pot kept by a live spin is not this replay's (2026-10-28).
    this._randomPotTrigger = null;
  }

  /**
   * Switch this object back to live mode. Does not by itself restore live
   * game state — that's Stage 5 (a fresh lightweight fetch via initGame()),
   * triggered from the History board's own close, not from here.
   */
  exitReplayMode() {
    this.replayMode = false;
    this.replayUrl = null;
    this.replayStepIds = null;
    this.replayMaxStepId = null;
    this.replayStepCursor = 0;
    this.replayLastStepId = null;
    this.replayBet = null;
    this.replayPeek = null;
    // ...nor is a replay's kept Random Pot the live game's (2026-10-28).
    this._randomPotTrigger = null;
  }

  /**
   * Read the NEXT recorded step's command without consuming it.
   *
   * ── Why this exists ──
   *
   * The gamble screen has to answer itself during a replay, and it can only do
   * that if it knows which button the player originally pressed. The recording
   * holds exactly that: the decision is a step of its own, carrying
   * `flow.command` of `gamble_freespins` or `collect_freespins` (verified on
   * round 1455068, whose step 2 is a collect).
   *
   * But reading it must not ADVANCE the round — the press that follows is what
   * consumes the step. So the payload is cached here and `_replaySpin` takes it
   * from the cache instead of re-fetching. One request either way.
   *
   * @returns {Promise<string|null>} the step's command, or null when there is
   *   no next step, this object is not replaying, or the fetch failed.
   */
  async peekReplayStepCommand() {
    if (!this.replayMode || !this.replayUrl) return null;

    const stepId = this._nextReplayStepId();
    if (stepId == null) return null;

    if (this.replayPeek?.stepId === stepId) {
      return this.replayPeek.data?.flow?.command ?? null;
    }

    try {
      const response = await requestJson(`${this.replayUrl}/${stepId}`, {
        headers: { Accept: "application/json" },
      });
      const data = response?.data;
      if (!data || typeof data !== "object") return null;
      this.replayPeek = { stepId, data };
      return data.flow?.command ?? null;
    } catch (error) {
      // Never throws. A peek is an optimisation for the presentation; failing
      // it must leave the round playable rather than break it, and the real
      // fetch below will report the failure properly when the step is taken.
      console.warn("[Replay] step peek failed:", error?.message || error);
      return null;
    }
  }

  /**
   * Stage 1 — fetch the replay round's step sequence (step_ids/max_step_id,
   * for replaySlice's step cursor), its own bet, and its own wallet snapshot
   * — both informational display only (spec §9: never merged into the live
   * wallet/bet, which stay completely untouched in state.game the whole time
   * replay is active; the UI just displays these instead while active).
   *
   * Deliberately lightweight: unlike the live boot sequence (initGame),
   * this does NOT reload assets/sounds, does NOT re-parse the full game
   * config into Redux, and does NOT touch localStorage. The live game's
   * config (paytable, reels, etc.) is already loaded and identical for a
   * replay of the same game — all replay actually needs from init is the
   * step sequence. initGame() itself stays completely untouched by this.
   *
   * Confirmed live against this game (docs/replay-history-spec.md §0): a
   * plain base-game round has step_ids:[1]; a bonus_buy round had
   * step_ids:[1,2,3,4,5,6,7] — one step per real gameplay action that
   * round took, base spin included.
   *
   * @returns {Promise<{stepIds: number[]|null, maxStepId: number|null, bet: number|null, balance: number|null}>}
   */
  async fetchReplayInit() {
    if (!this.replayUrl) {
      throw new Error("PopiPlay: not in replay mode (call enterReplayMode first)");
    }

    try {
      const response = await requestJson(this.replayUrl, {
        headers: { Accept: "application/json" },
      });

      const data = response?.data;
      if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      const stepIds = Array.isArray(data?.step_ids) ? data.step_ids : null;
      const maxStepId =
        typeof data?.max_step_id === "number" ? data.max_step_id : null;
      const bet = typeof data?.bet === "number" ? data.bet : null;
      const balance =
        typeof data?.balance?.wallet === "number" ? data.balance.wallet : null;
      // Stash on this — _replaySpin()/_nextReplayStepId() below read from
      // here, not from Redux (this object has no Redux access, and keeping
      // the cursor self-contained means spin()'s replay branch needs no new
      // params threaded through the shared rgsPlay call chain).
      this.replayStepIds = stepIds;
      this.replayMaxStepId = maxStepId;
      this.replayStepCursor = 0;
      this.replayLastStepId = null;
      this.replayBet = bet;

      // Free rounds / casino rounds — the init response reports the campaign
      // state AS IT WAS at the start of this round (verified live 2026-08-11:
      // `free_rounds:{status:"active", bet_value, performed, count}` plus the
      // running total in `balance.free_rounds`). Returned here so the FR
      // counter and Total Win are on screen the moment the Enter modal closes,
      // rather than only appearing once the first replay step is fetched.
      const freeRounds = data?.free_rounds || null;
      const freeRoundsTotalWin = data?.balance?.free_rounds ?? 0;

      return { stepIds, maxStepId, bet, balance, freeRounds, freeRoundsTotalWin };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        throw error;
      }
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Replay init call failed: ${error.message}`);
    }
  }

  /**
   * Standalone replay link boot (docs/replay-history-spec.md §0, Stage 9) —
   * used ONLY when the page itself was launched as a replay link
   * (`window.__OPTIONS__.server_id === "replay"`, see
   * launcherOptions.isStandaloneReplayMode()). There is no live session to
   * validate or restore in this mode, so unlike initGame() above this:
   *  - GETs `replayUrl` directly (no POST, no play_token/CSRF headers —
   *    confirmed live 2026-08-10 that both are empty in this launch mode's
   *    __OPTIONS__, so initGame()'s own missing-fields check would throw
   *    immediately if called here instead).
   *  - Never touches StorageManager — none of the live reload-recovery
   *    concerns baked into initGame() (mid-bonus respin, free rounds,
   *    base-game round persistence) apply to a replay session; every page
   *    load starts fresh at step one (confirmed §0: "no localStorage
   *    involvement at all" for replay).
   *  - Puts this object straight into replay mode and primes the step
   *    cursor itself (the same fields fetchReplayInit sets) — this single
   *    call does the equivalent of enterReplayMode()+fetchReplayInit() from
   *    the in-game hot-swap path, plus the full config parse initGame()
   *    would otherwise have done, in one request.
   *
   * Returns the same result shape initGame() does, so gameSettingsSlice and
   * every sibling reducer on initializeGame.fulfilled (freeRoundsSlice,
   * spinSlice, potCollectionSlice, featureGameSettingsSlice) work completely
   * unchanged. featureState/round intentionally reflect a fresh base-game
   * boot with no outcome yet — the init response never has one
   * (docs/replay-history-data-contract.md §2, confirmed live); the first
   * replay spin's own _replaySpin() supplies the real featureState/round
   * once fetched, exactly like a live init→first-spin handoff.
   *
   * @param {string} replayUrl
   * @returns {Promise<object>} same result shape as initGame()
   */
  async initFromReplayLink(replayUrl) {
    if (!replayUrl) {
      throw new Error("PopiPlay: standalone replay link has no replayUrl");
    }

    try {
      const response = await requestJson(replayUrl, {
        headers: { Accept: "application/json" },
      });

      const data = response?.data;
      if (!data || typeof data !== "object") {
        throw new Error("PopiPlay: Invalid replay init response");
      }
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      const options = data.options;
      const availableBets = options?.available_bets;
      const defaultBet = options?.default_bet;
      const baseBet = options?.base_bet;
      const paytable = options?.paytable;
      const lines = options?.lines;
      const currencyObj = options?.currency;
      const wallet = data.balance?.wallet;
      const jackpotMultipliers =
        options?.jackpots || options?.feature_options?.jackpots || null;
      const coinValues = options?.coin_values || null;
      this.jackpots = jackpotMultipliers;

      // NOTE: options.default_bet/base_bet are NOT hard-required here, unlike
      // initGame()'s live validation — the confirmed replay init contract
      // (docs/replay-history-data-contract.md §2) only lists available_bets/
      // paytable/reels/screen/lines/jackpots/coin_values/feature_options/
      // special_symbols/layout/currency under options; default_bet/base_bet
      // are live-boot concepts ("what bet does a NEW player see") that a
      // replay init response may simply omit — a replay already has its own
      // bet from the top-level `bet` field. gameSettingsSlice.js itself
      // already tolerates a missing baseBet (`result.baseBet || 1`), so this
      // matches that tolerance instead of live initGame()'s stricter check.
      const missingFields = [];
      if (!availableBets || !Array.isArray(availableBets)) missingFields.push("options.available_bets");
      if (!paytable || typeof paytable !== "object") missingFields.push("options.paytable");
      if (!lines || !Array.isArray(lines)) missingFields.push("options.lines");
      if (typeof wallet !== "number") missingFields.push("balance.wallet");
      if (!currencyObj || typeof currencyObj.code !== "string") missingFields.push("options.currency.code");
      if (missingFields.length > 0) {
        throw new Error(`PopiPlay: Invalid replay init response — missing [${missingFields.join(", ")}]`);
      }

      const currencyConfig = {
        subunits: currencyObj.subunits || 100,
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2,
        symbol: currencyObj.symbol || currencyObj.code || "",
      };

      // Prefer the round's own bet (top-level, always present per the
      // confirmed contract) — defaultBet is only a fallback for a malformed
      // response, baseBet defaults like gameSettingsSlice.js's own `|| 1`.
      const bet =
        typeof data.bet === "number"
          ? data.bet
          : typeof defaultBet === "number"
            ? defaultBet
            : 0;
      const safeBaseBet = typeof baseBet === "number" ? baseBet : 1;
      const round = this.extractRoundFromInit(data);

      // Prime replay mode + step cursor directly — equivalent of
      // enterReplayMode()+fetchReplayInit() for the in-game path (§0 Stage 0/1).
      this.replayMode = true;
      this.replayUrl = replayUrl;
      this.replayStepIds = Array.isArray(data.step_ids) ? data.step_ids : null;
      this.replayMaxStepId = typeof data.max_step_id === "number" ? data.max_step_id : null;
      this.replayStepCursor = 0;
      this.replayLastStepId = null;
      this.replayBet = bet;
      // The stake coin values and Hold & Win money scale by — the live init
      // sets it, the standalone link did not (2026-10-28).
      // this.baseBet = safeBaseBet;
      // The SERVER's value only (2026-10-28 review): safeBaseBet is 1 when the
      // init leaves base_bet out, which made every coin 20x too big —
      // assignCoinValues falls back to the contract's own 20 on null.
      this.baseBet = typeof baseBet === "number" && baseBet > 0 ? baseBet : null;
      // No Random Pot kept from before this page's replay.
      this._randomPotTrigger = null;

      return {
        balance: wallet,
        bet,
        baseBet: safeBaseBet,
        bets: availableBets,
        round,
        token: null,
        currency: currencyObj.code,
        currencyConfig,
        paytable: Object.fromEntries(
          Object.entries(paytable).map(([apiId, values]) => [
            PROVIDER_CONFIG.SYMBOL_MAP[apiId] ?? apiId,
            values,
          ])
        ),
        winningLines: lines,
        bonusBuyMultiplier: undefined,
        bonusBuyExtraMultiplier: undefined,
        bonusBuyMultipliers: undefined,
        bonusBuyFeatureName: undefined,
        bonusBuyFeatures: undefined,
        game: window.__OPTIONS__?.identifier,
        gameMode: "base",
        featureState: { type: null, phase: null, data: null },
        reloadState: { needsReload: false },
        availableActions: data.flow?.available_actions || ["init", "spin"],
        flowState: data.flow?.state || "ready",
        features: data.features || null,
        // Free rounds are deliberately NOT surfaced here. This result is
        // consumed by initializeGame.fulfilled — freeRoundsSlice's LIVE init
        // reducer, which reads the player's real StorageManager, sets
        // reloadPending, and on status "played" raises completionPending,
        // which would fire the campaign's completion popups at boot before a
        // single step has played. The standalone boot dispatches
        // fetchReplayInit right after this, and its replay-specific reducer
        // seeds the same indicators without any of that live bookkeeping.
        freeRounds: null,
        freeRoundsTotalWin: 0,
        command: "init",
        jackpotMultipliers,
        coinValues,
        progress: data.progress || null,
      };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) throw error;
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Standalone replay init failed: ${error.message}`);
    }
  }

  /**
   * The step id to fetch next, or null once the round has no more steps.
   * Mirrors selectNextReplayStepId in replaySlice.js exactly (spec §6.2
   * precedence) — that copy is the UI-facing "are we done" signal for the
   * future Ended-modal trigger (Stage 4/5), this one is what _replaySpin()
   * actually acts on. Two copies because this object has no Redux access;
   * if they ever need to drift apart, that's a smell — keep them in sync.
   */
  _nextReplayStepId() {
    const { replayStepIds, replayStepCursor, replayMaxStepId, replayLastStepId } = this;

    if (Array.isArray(replayStepIds)) {
      return replayStepCursor < replayStepIds.length
        ? replayStepIds[replayStepCursor]
        : null;
    }
    if (typeof replayMaxStepId === "number" && typeof replayLastStepId === "number") {
      return replayLastStepId < replayMaxStepId ? replayLastStepId + 1 : null;
    }
    return replayLastStepId == null ? 1 : null;
  }

  /** Called after a successful replay step fetch to move the cursor forward. */
  _advanceReplayStep(stepId) {
    if (Array.isArray(this.replayStepIds)) {
      this.replayStepCursor += 1;
    }
    this.replayLastStepId = stepId;
  }

  /**
   * Stage 2/3 — replay's answer to spin(). Fetches the next recorded step
   * (GET {replayUrl}/{stepId}) instead of doing a real spin, and runs it
   * through the SAME core transform pipeline the live path uses
   * (normalizeScreenData → extractRound → detectFeatureState →
   * adaptSpinRoundToState) so downstream consumers (gameSettingsSlice,
   * potCollectionSlice, featureGameSettingsSlice, ReelControlContext's
   * stop-detection effect) treat the result identically to a live spin.
   *
   * Deliberately does NOT replicate, on purpose:
   * - Any StorageManager read/write (spec §9 — replay must never touch the
   *   real save/reload state; this is the one live-path behavior that
   *   would be actively harmful to carry over, not just unnecessary).
   * - Free-rounds-specific bookkeeping — live-only state tracking.
   * - this.currentToken — never overwritten here, so a live spin right
   *   after exiting replay isn't sent with a stale replay round_id.
   *
   * The bonus-trigger synthetic coin win IS replicated (see below). It was
   * skipped on the first pass as "cosmetic"; that was wrong — it's what
   * carries the Pot Collection intro popup, so without it a replayed bonus
   * round shows its trigger screen and then never enters the bonus.
   *
   * RESOLVED (2026-08-10, real bonus_buy replay step inspected):
   * detectFeatureState()'s bonus-phase check needs
   * features.respins_left/respin_game_type on top of flow.state, and a
   * replay step DOES carry all three (`respins_left:3`,
   * `respin_game_type:"DMC"`, `flow.state:"respin"`, `flow.command:"spin"`)
   * alongside the full trigger payload (initial_respin_screen/_values/
   * _original_values, trigger_coordinates, initial_events, initial_coins_count).
   * So bonus phase detection on replay steps works exactly as it does live.
   *
   * @returns {Promise<object>} shaped like a live spin() result
   */
  async _replaySpin() {
    let stepId = this._nextReplayStepId();
    if (stepId == null) {
      // Cursor exhausted, but a spin still arrived — wrap back to step one
      // rather than throwing. Throwing here freezes the reels: they were
      // already started by handleSpin, and a rejected rgsPlay never delivers
      // stop positions, so the game looks permanently stuck with no way out.
      //
      // Wrapping is also exactly the intended semantics for the standalone
      // replay link (§0 Stage 9): that page loops the one round forever, and
      // its "Replay Again" resets this cursor via fetchReplayInit — so any
      // spin arriving before that async reset lands (or after it failed)
      // should simply restart the round, which is what the player asked for.
      //
      // Never reached on the in-game hot-swap path in normal operation:
      // handleSpin's replayFinished guard opens the Ended modal instead of
      // spinning once the steps run out. This is the safety net for the
      // race, not the primary flow.
      this.replayStepCursor = 0;
      this.replayLastStepId = null;
      stepId = this._nextReplayStepId();
      if (stepId == null) {
        throw new Error("PopiPlay: replay round has no steps to play");
      }
    }

    try {
      // Already fetched by peekReplayStepCommand? Take it, and clear it — a
      // cached payload is good for exactly the step it was read for.
      let response;
      if (this.replayPeek?.stepId === stepId) {
        response = { data: this.replayPeek.data };
        this.replayPeek = null;
      } else {
        this.replayPeek = null;
        response = await requestJson(`${this.replayUrl}/${stepId}`, {
          headers: { Accept: "application/json" },
        });
      }

      const data = response?.data;
      if (!data || typeof data !== "object") {
        throw new Error("PopiPlay: Replay step returned an invalid response");
      }
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      if (data.outcome && Array.isArray(data.outcome.screen)) {
        const normalized = normalizeScreenData(data.outcome.screen);
        data.outcome.reels = normalized;
        data.outcome.screen = normalized;
      }

      let normalizedOutcome = data.outcome;
      if (data.outcome) {
        const handler = this.getResponseHandler();
        normalizedOutcome = handler.patterns.regular.extractRound(data.outcome);
        // ── Which field is "the win" depends on the STEP's command ──
        //
        // The live path's rule, applied to the step's own recorded command
        // instead of a live request's. See the long note in spin(): a Mad Labs
        // `freespin` pays in `outcome.win` while `balance.game` accumulates the
        // whole round, so reading the accumulator makes every free spin report
        // the running total as its own win.
        //
        // This branch read the accumulator unconditionally, and that BRICKED
        // every replayed free-spins round. The spin that pays nothing still
        // reported the round's total, so `freeSpins.currentRound.win` was
        // non-zero with `wins: []` — the FS win controller found nothing to
        // match and released its lock, but the loop gate is
        // `roundWin > 0 && !counterFinished` (FreeSpinBasicControlContext), and
        // no counter ever runs for a round that did not pay. The loop waited
        // for ever. Observed on round 1455068, step 5.
        //
        // Everything the live note lists was wrong here too, just less visibly:
        // the Total Win row double-counted, and the win tier was judged on the
        // round's running total, so an ordinary spin late in a round could
        // cross Big Win — or MAX WIN.
        const stepCommand = data.flow?.command;
        normalizedOutcome.win =
          stepCommand === "freespin"
            ? (normalizedOutcome.win ?? 0)
            : (data.balance?.game ?? normalizedOutcome.win);
      }

      // Round id — same as the live path (normalizedOutcome.id below the live
      // transform). A step reports a short numeric flow.round_id; the Ended
      // modal shows it in place of a played-at date on standalone links,
      // which have no history row to take a timestamp from.
      if (normalizedOutcome && data.flow?.round_id != null) {
        normalizedOutcome.id = data.flow.round_id;
      }

      // const anticipation = data.features?.anticipation;
      // The live path's fallback too (2026-10-28: "how game plays now, history
      // must play it").
      const anticipation = data.features?.anticipation ?? data.anticipation;
      if (normalizedOutcome && anticipation !== undefined) {
        normalizedOutcome.anticipation = anticipation;
      }

      const featureState = this.detectFeatureState(data);
      // ── Free spins must be published BEFORE the game mode is decided ──
      //
      // The live path passes this second argument; this branch did not, and the
      // omission is why a replayed free-spins round did not behave like the
      // game. `featureState` only ever describes POT COLLECTION — a Mad Labs
      // free spin has none — so with no freeSpinsState every replayed free spin
      // reported `gameMode: "base"`, where live reports "feature".
      //
      // gameSettingsSlice writes the round into the BASE game on that flag:
      //
      //   const isFeatureSpin = gameMode === "feature" || state.gameMode === "feature";
      //   if (round && !isFeatureSpin) state.currentRound = ...
      //
      // So every free spin's board and wins were written into the base game's
      // round. The room hid it while it was open, and the moment the Total Win
      // closed the base game presented the LAST free spin on the base board —
      // the "swallowed" ending: round 1455068's final spin, 99 across 18 lines,
      // cycling again with the room gone.
      //
      // It also left `state.gameMode` at "base" for the whole feature, so
      // selectIsFeatureActive was false throughout a replayed bonus.
      //
      // Computed once here and reused for the `freeSpinsState` field below, so
      // the two can never disagree about the same step.
      const spinFreeSpinsState = this.detectFreeSpinsState(data);
      const gameMode = this.getGameMode(featureState, spinFreeSpinsState);
      const roundBet = data.outcome?.bet ?? this.replayBet ?? 0;
      const adaptedRound = this.adaptSpinRoundToState(normalizedOutcome, roundBet);
      // adaptSpinRoundToState builds a fresh round object and doesn't carry
      // `id` through (the live path never needs it past that point), but the
      // adapted round is what's returned below — so re-stamp it here rather
      // than widening the shared adapter, which every provider uses.
      if (adaptedRound && normalizedOutcome?.id != null) {
        adaptedRound.id = normalizedOutcome.id;
      }
      // Same condition live spin() uses (RGS.js isBonusTrigger), sourced from
      // this step's own recorded command instead of a live request's command
      // param: a bonus-trigger step commonly wins 0 immediately (payout
      // accrues over the respins that follow), so `win > 0` alone would miss
      // it — useWinController needs triggerWin to fire the reveal regardless.
      const isBonusTrigger =
        featureState.type === "bonus" &&
        featureState.phase === "intro" &&
        data.flow?.command === "spin";
      const isBonusRespin =
        featureState.type === "bonus" && data.flow?.command === "respin";

      // Attach the step's own round to featureState — potCollectionSlice reads
      // featureState.data.currentRound on both the trigger and every respin
      // (rgsPlay.fulfilled) to seed/advance the bonus grid. Mirrors the live
      // path; `data.outcome.screen` was already normalized in place above, so
      // it's used directly rather than re-normalized.
      if (featureState.type === "bonus" && (isBonusTrigger || isBonusRespin)) {
        const stepScreen = data.outcome?.screen;
        if (Array.isArray(stepScreen)) {
          featureState.data.currentRound = {
            reels: stepScreen,
            wins: normalizedOutcome?.wins || [],
            win: normalizedOutcome?.win || 0,
            bet: normalizedOutcome?.bet,
            storage: data.outcome?.storage || null,
          };
        }
      }

      // ── Cleopatra's Fire: the live spin's own steps, on the STEP's command
      // (2026-10-28: "how game plays now, history must play it") ── copied
      // from spin() so a replayed round is built exactly as it was played.
      const replayCommand = data.flow?.command || "spin";
      // The free-spins retrigger: 2+ scatters on a free spin (live spin()).
      const retriggerScatters =
        replayCommand === "freespin"
          ? scanBoardScatters(adaptedRound?.reels ?? data.outcome?.screen).scatters.length
          : 0;
      const freeSpinsRetrigger =
        retriggerScatters >= 2
          ? Math.max(0, Number(data.outcome?.freespins_issued) || 0) ||
            (retriggerScatters >= 3 ? 5 : 3)
          : 0;
      // The Hold & Win trigger: its coins lit (so the intro opens) and its
      // jackpot coins marked (live spin()). Not for a Random Pot.
      const holdAndWinTrigger = parseHoldAndWinState(data);
      const isHoldAndWinTrigger =
        holdAndWinTrigger?.phase === "trigger" && !holdAndWinTrigger.randomPot;
      if (adaptedRound && isHoldAndWinTrigger) {
        const triggerCoinCells = holdAndWinTrigger.newCells.map((cell) => [
          cell.reel,
          toReelRow(cell.row),
        ]);
        adaptedRound.wins = withTriggerCoinWin(
          adaptedRound.wins,
          triggerCoinCells.length ? triggerCoinCells : scanBoardCoins(adaptedRound.reels),
        );
        markJackpotCoins(
          adaptedRound,
          triggerCellsOnReels(data.outcome?.storage?.new_cells),
          this.jackpots,
        );
      }
      // Every coin's number (live spin()).
      if (adaptedRound) {
        assignCoinValues(
          adaptedRound,
          isHoldAndWinTrigger ? triggerCellsOnReels(data.outcome?.storage?.new_cells) : null,
          data.outcome?.bet,
          this.baseBet,
        );
      }

      // Synthetic scatter win on a replayed trigger step — NOT cosmetic,
      // load-bearing. A trigger step reports `outcome.wins: []` because the
      // payout accrues later, so without manufacturing this win the trigger
      // screen renders and the game never enters the feature at all. Same
      // helper as the live path, so a replay animates what the live spin did.
      let isFreeSpinTrigger = false;
      let hasRevealSequence = false;
      if (adaptedRound) {
        // Both trigger sources, counted TOGETHER — see the live path's note.
        const triggerPositions = collectAllTriggerPositions(adaptedRound);
        const goldenAward = goldenGambleTriggerAward(adaptedRound);
        if (triggerPositions.length > 0) {
          // SC1 means the reveal SEQUENCE plays — always, the same show
          // whatever it lands on, and wherever the scatter came from. What
          // decides between a package and the BLANK window is the server's
          // award evidence, and only the TRIGGER (entering the feature) is
          // gated on it. GDD §8.
          // A complete trigger always contains SC1 — that is what makes
            // it complete — so the reveal is owed whenever there is one.
            hasRevealSequence = isGambleEnabled();
          // isFreeSpinTrigger =
          //   hasFreeSpinAwardEvidence(data.features) || !!goldenAward;
          // A FREE SPIN never triggers free spins — its scatters are a
          // retrigger (live spin(); `freespins_issued` is on every free spin).
          isFreeSpinTrigger =
            replayCommand !== "freespin" &&
            (hasFreeSpinAwardEvidence(data.features) || !!goldenAward);
          adaptedRound.wins = withTriggerScatterWin(
            adaptedRound.wins,
            triggerPositions,
          );
          // Same reveal payload as the live path, so a replayed trigger spins
          // its strip onto the package the original spin actually awarded —
          // or onto the blank when it awarded nothing.
          if (isGambleEnabled()) {
            adaptedRound.gambleScatter = buildGambleScatterReveal(
              data.features,
              data.outcome,
              triggerPositions,
              goldenAward,
            );
          }
        }
      }

      if (adaptedRound) {
        // A retrigger's 0-pay scatter line — the Additional Free Spins popup
        // opens off it (live spin()).
        if (freeSpinsRetrigger > 0) {
          adaptedRound.wins = withTriggerScatterWin(
            adaptedRound.wins,
            scanBoardScatters(adaptedRound.reels).scatters,
          );
        }
        // hasRevealSequence: a blank reveal wins nothing and triggers nothing,
        // but the presentation must still run — useWinController gates on this.
        // adaptedRound.triggerWin =
        //   adaptedRound.win > 0 ||
        //   isBonusTrigger ||
        //   isFreeSpinTrigger ||
        //   hasRevealSequence;
        adaptedRound.triggerWin =
          adaptedRound.win > 0 ||
          isBonusTrigger ||
          isFreeSpinTrigger ||
          freeSpinsRetrigger > 0 ||
          isHoldAndWinTrigger ||
          hasRevealSequence;
        adaptedRound.freeSpinsRetrigger = freeSpinsRetrigger;

        // The show, separated from the win — see the live path in spin() for
        // why. Set here too so a REPLAYED trigger sequences exactly like the
        // spin it recorded, rather than dimming up front.
        adaptedRound.triggerReveal = hasRevealSequence;
      }

      this._advanceReplayStep(stepId);
      // Checked AFTER advancing — true once this was the last recorded step,
      // the signal replaySlice/ReelControlContext use to show the Ended
      // modal instead of allowing another spin (docs/replay-history-spec.md
      // §4). Not consumed by anything live-side; harmless extra field there.
      const replayFinished = this._nextReplayStepId() == null;

      // return {
      const result = {
        token: this.currentToken,
        // No bet/balance fields on purpose — gameSettingsSlice.js's
        // rgsPlay.fulfilled writes state.game.bet/state.game.balance
        // unconditionally whenever either is present (its balance-strategy
        // comment is aspirational, not actually gated on
        // getBalanceStrategy()'s flags), so returning the replayed round's
        // own bet/balance here would silently overwrite the player's real
        // live bet/balance in Redux — e.g. replaying an old round placed at
        // 5.00 would leave their live bet stuck at 5.00 after exiting, with
        // no restore path. The replay bet/balance display is already
        // handled separately and correctly via replaySlice.replayBet/
        // replayBalance (Stage 1) — roundBet above (used for adaptedRound)
        // covers everything actually needed from this value.
        bet: undefined,
        balance: undefined,
        // This step's wallet under a replay-only field name, so it reaches
        // replaySlice (which shows it) without going near the live
        // `balance` field above that gameSettingsSlice would write straight
        // into the player's real wallet. Verified live (2026-08-10): a step
        // reports the wallet with the bet already deducted and the win not
        // yet credited.
        replayWallet:
          typeof data.balance?.wallet === "number" ? data.balance.wallet : null,
        round: adaptedRound || normalizedOutcome,
        gameMode,
        featureState,
        // ── In a replay the STEP LIST is the permission, not the server ──
        //
        // spinSlice refuses a free spin locally unless "freespin" is in
        // available_actions — a real guard, added because sending an action the
        // server has closed returns 202 twice and bans the player (see the
        // respin-ban work). A replay cannot be banned: it is a recording, and
        // the authority for "may I spin again" is whether another step exists,
        // which `replayFinished` below already answers.
        //
        // So when a step is itself inside free spins and the replay has not run
        // out of steps, "freespin" is guaranteed present. Without this the
        // round's loop asks for a spin, is refused locally, and the replayed
        // bonus stalls on its first spin.
        //
        // Narrow on purpose: only this one action, only while the step's own
        // flow.state says free spins. NOT VERIFIED against a live replay
        // payload — if the recording does carry available_actions this is a
        // harmless no-op, and if it does not, this is what makes it work.
        availableActions: (() => {
          const fromStep = data.flow?.available_actions;
          const base =
            Array.isArray(fromStep) && fromStep.length > 0
              ? [...fromStep]
              : ["init", "spin"];
          const inFreeSpins = data.flow?.state === "freespins";
          if (inFreeSpins && !replayFinished && !base.includes("freespin")) {
            base.push("freespin");
          }
          // ...and Hold & Win's respins the same (2026-10-28): its loop is
          // refused locally without "respin" (canSendRespin, rgsPlay).
          if (data.flow?.state === "respin" && !replayFinished && !base.includes("respin")) {
            base.push("respin");
          }
          return base;
        })(),

        flowState: data.flow?.state || "ready",
        features: data.features || null,
        // holdAndWin: parseHoldAndWinState(data),
        holdAndWin: holdAndWinTrigger,
        // Same passthrough as initFromReplayLink — see its comment. Returning
        // null here isn't neutral: freeRoundsSlice reads null as "FR ended"
        // and clears the counter, so a replayed free round would switch its
        // own indicators off mid-round.
        freeRounds: data.free_rounds || null,
        freeRoundsTotalWin: data.balance?.free_rounds ?? null,
        // ── The STEP's own command, not a hardcoded "spin" ──
        //
        // This was `"spin"` for every step, which is what stopped a bonus
        // ROUND from replaying at all. Two consumers read it and both were
        // being lied to:
        //
        //   spinSlice   kindOfCommand classifies `spin` as BASE and
        //               respin/freespin as FEATURE. A replayed free spin
        //               therefore moved the BASE isSpinning/spinComplete —
        //               precisely the leak spinCommands.js was written to
        //               stop.
        //   freeSpinsSlice  gates its entire per-spin branch on
        //               `command === "freespin"`, so the board, the counters,
        //               `played` and `totalWin` were never touched. The room
        //               opened, the loop fired, the step advanced the reels,
        //               and `played` never moved — the round could not end.
        //
        // Falls back to "spin" for a step that names nothing, which is what
        // every step reported before this change.
        //
        // NOTE for Pot Collection: its respin steps now report "respin"
        // instead of "spin", which is what a LIVE respin reports. Its engine
        // drives off featureState rather than the command, so this should be
        // a correction rather than a change — but it is a behaviour change to
        // a tested feature and deserves a re-test.
        command: data.flow?.command || "spin",
        // The normalized free-spins state, published exactly as the live spin
        // path publishes it (see `freeSpinsState: spinFreeSpinsState`). This is
        // the `fsData` freeSpinsSlice reads for issued / left / played /
        // totalWin / fsState / winLimitReached. Its absence here is the other
        // half of why a replayed bonus round stalled.
        freeSpinsState: spinFreeSpinsState,
        // The ladder, published on every step exactly as the live path does —
        // freeSpinsSlice reads it for the settled tier, and the package the
        // arrival card wears comes off it.
        gamble: parseGambleState(data.features),
        // ── What ARMS the round ──
        //
        // freeSpinsSlice raises `triggered` from this and nothing else, so
        // without it a replayed trigger step revealed its scatters and then
        // stopped: no room, no transition, no round. Built from the same
        // `gambleScatter` payload the reveal above already needed, so the
        // package replayed is the package the original spin awarded.
        //
        // Emitted ONLY on the triggering step, which is what stops a later
        // step from re-arming a round already running.
        freeSpinsTrigger: isFreeSpinTrigger
          ? {
              spins: adaptedRound?.gambleScatter?.value ?? 0,
              tier: adaptedRound?.gambleScatter?.tier ?? null,
              reveal: adaptedRound?.gambleScatter ?? null,
            }
          : undefined,
        progress: data?.progress || null,
        replayFinished,
      };

      // ── The Random Pot, kept for its presentation spin (2026-10-28) ── as
      // the live spin() keeps it: the presentation is not a step of its own
      // (randomPotPresentation hands the kept result back), so without this
      // a replayed Random Pot stopped at "no_random_pot".
      this._randomPotTrigger =
        result.holdAndWin?.phase === "trigger" && result.holdAndWin?.randomPot
          ? {
              result,
              rawNewCells: triggerCellsOnReels(data.outcome?.storage?.new_cells) || [],
              bet: data.outcome?.bet,
            }
          : null;
      return result;
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) throw error;
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Replay step call failed: ${error.message}`);
    }
  }

  // ── Rounds History (Replay & History panel) ──

  /**
   * Absolute URL for the rounds-history GET, or null when the launcher didn't
   * provide one. The link comes pre-signed in
   * window.__OPTIONS__.actions.history.link and is normally relative, so it
   * resolves against the API origin (`api` is a full URL with path + query,
   * hence origin only). `actions.history.mode` is "api" for this transport.
   * @returns {string|null}
   */
  getRoundsHistoryUrl() {
    const link = window.__OPTIONS__?.actions?.history?.link;
    if (!link || typeof link !== "string") return null;
    if (/^https?:\/\//i.test(link)) return link;

    const api = window.__OPTIONS__?.api;
    if (!api) return null;
    try {
      const origin = new URL(api).origin;
      return `${origin}${link.startsWith("/") ? "" : "/"}${link}`;
    } catch {
      return null;
    }
  }

  /**
   * Pull the replay API base out of a row's `replay_link`.
   * The link is normally a launcher URL carrying a `replayUrl` query param;
   * when there's no `game` param the link itself is the API URL.
   */
  _extractReplayUrl(replayLink) {
    if (!replayLink || typeof replayLink !== "string") return null;
    try {
      const url = new URL(replayLink);
      const explicit = url.searchParams.get("replayUrl");
      if (explicit) return explicit;
      return url.searchParams.get("game") ? null : replayLink;
    } catch {
      return null;
    }
  }

  /** Round id carried in the row's `replay_link` query string. */
  _extractRoundId(replayLink) {
    if (!replayLink || typeof replayLink !== "string") return null;
    try {
      return new URL(replayLink).searchParams.get("round_id");
    } catch {
      return null;
    }
  }

  /**
   * Fetch the player's past rounds for the History panel.
   *
   * No auth headers are needed — the token is baked into the signed path.
   * Response is either a bare array or an object wrapping `rounds`.
   *
   * Amounts stay in RAW SUBUNITS (formatting happens at render, like
   * state.game). The signed `replayUrl` / `roundId` are re-issued on every
   * call, so results must never be cached.
   *
   * @returns {Promise<Array<object>>} rows in server order (newest first)
   */
  async fetchRoundsHistory() {
    const historyUrl = this.getRoundsHistoryUrl();
    if (!historyUrl) {
      throw new Error("PopiPlay: actions.history.link is not configured");
    }

    try {
      const response = await requestJson(historyUrl, {
        headers: { Accept: "application/json" },
      });

      const data = response?.data;
      if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      const rawRows = Array.isArray(data) ? data : data?.rounds;
      if (!Array.isArray(rawRows)) return [];

      return rawRows.map((row) => ({
        time: row?.time ?? null,
        bet: Number(row?.bet) || 0,
        win: Number(row?.win) || 0,
        initialBet: Number(row?.initial_bet) || Number(row?.bet) || 0,
        currency: row?.currency ?? null,
        feature: row?.feature ?? null,
        roundId: this._extractRoundId(row?.replay_link),
        replayUrl: this._extractReplayUrl(row?.replay_link),
        // Full shareable launcher URL, kept as-is (unlike replayUrl above,
        // which is just the API base extracted out of it) — needed for the
        // Ended modal's "Copy replay link" (spec §6.3).
        replayLink: row?.replay_link ?? null,
      }));
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        throw error;
      }
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Rounds history API call failed: ${error.message}`);
    }
  }

  // ── Spin ──

  spin(bet, token, command, purchasedFeature, spinOptions) {
    // Replay branch (docs/replay-history-spec.md §0, Stage 2) — every param
    // above comes from live state (state.game/state.freeRounds via
    // getSpinParams), all irrelevant here: the next replay step is fixed by
    // the recording, not by what a live spin would have asked for. See
    // _replaySpin() for what this actually does instead.
    if (this.replayMode) {
      return this._replaySpin();
    }

    return new Promise((resolve, reject) => {
      if (!this.worker) {
        this.worker = createInlineWorker(spinWorkerCode);
      }

      const options = window.__OPTIONS__;
      if (!options) {
        reject(new Error("PopiPlay: window.__OPTIONS__ not found."));
        return;
      }

      const apiUrl = options.api;
      const csrfTokenHeaderName = options.csrfTokenHeaderName;
      const csrfTokenHeaderValue = options.csrfTokenHeaderValue;
      const identifier = options.identifier;
      const playToken = options.play_token;

      const missingFields = [];
      if (!apiUrl) missingFields.push("api");
      if (!csrfTokenHeaderName) missingFields.push("csrfTokenHeaderName");
      if (!csrfTokenHeaderValue) missingFields.push("csrfTokenHeaderValue");
      if (!identifier) missingFields.push("identifier");

      if (missingFields.length > 0) {
        reject(new Error(`PopiPlay: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`));
        return;
      }

      this.worker.onmessage = (event) => {
        const data = event.data;

        if (data.error) {
          let errorCode = null;
          if (typeof data.error === "object" && data.error.errors && Array.isArray(data.error.errors)) {
            errorCode = data.error.errors[0]?.code;
          }

          if (errorCode && PROVIDER_CONFIG.ERROR_CODES[errorCode] !== undefined) {
            errorCode = PROVIDER_CONFIG.ERROR_CODES[errorCode];
            if (data.error.errors && data.error.errors[0]) {
              data.error.errors[0].code = errorCode;
            }
          }

          let extractedBalance = null;
          if (typeof data.error === "object" && data.error.balance) {
            if (typeof data.error.balance === "object" && data.error.balance.wallet !== undefined) {
              extractedBalance = data.error.balance.wallet;
            } else if (typeof data.error.balance === "number") {
              extractedBalance = data.error.balance;
            }
          }

          if (extractedBalance !== null && typeof data.error === "object") {
            data.error.balance = extractedBalance;
          }

          if (typeof data.error === "object" && data.error.errors) {
            reject(data.error);
          } else {
            const normalizedError = {
              errors: [{ code: errorCode || 999, desc: typeof data.error === "string" ? data.error : "Unknown error" }],
            };
            if (extractedBalance !== null) normalizedError.balance = extractedBalance;
            reject(normalizedError);
          }
          return;
        }

        // Keep raw screen before normalization (needed for bonus symbol parsing)
        const rawScreen = data.outcome?.screen
          ? data.outcome.screen.map((reel) => [...reel])
          : null;

        // Normalize symbol IDs
        if (data.outcome && Array.isArray(data.outcome.screen)) {
          const normalized = normalizeScreenData(data.outcome.screen);
          data.outcome.reels = normalized;
          data.outcome.screen = normalized;
        }
        const transformedData = data;
        traceServerMoney(command, transformedData);

        if (transformedData.flow?.round_id) {
          this.currentToken = transformedData.flow.round_id;
        }

        let extractedBalance = transformedData.balance;
        if (transformedData.balance && typeof transformedData.balance === "object" && transformedData.balance.wallet !== undefined) {
          extractedBalance = transformedData.balance.wallet;
        }

        let normalizedOutcome = transformedData.outcome;
        if (transformedData.outcome) {
          const handler = this.getResponseHandler();
          normalizedOutcome = handler.patterns.regular.extractRound(transformedData.outcome);
          // ── Which field is "the win" depends on the feature ──
          //
          // Pot Collection's respins reset `outcome.win` to 0 and accumulate in
          // `balance.game`, so there the accumulator IS the round's win and
          // reading it is correct.
          //
          // A Mad Labs `freespin` is the opposite, and taking the accumulator
          // there was a core defect. Verified from a live init, round 1422828,
          // spin 4 of 9: `outcome.win` 1730 — what THIS spin paid — while
          // `balance.game` 2067 is the whole round so far. Every free spin
          // therefore reported the running total as its own win, which:
          //
          //   - double-counted the Total Win row, since freeSpinsSlice adds
          //     `round.win` to `totalWin` on every spin;
          //   - inflated the win tier, so an ordinary spin late in a round
          //     crossed the Big Win thresholds on the round's total rather
          //     than on its own pay, and would eventually reach MAX WIN;
          //   - printed the wrong number in WinText, and one that no
          //     combination of the lines below it could add up to.
          //
          // Keyed off the command, not the feature state: a `freespin` is the
          // only command with a per-spin outcome inside an accumulating round.
          normalizedOutcome.win =
            command === "freespin"
              ? (normalizedOutcome.win ?? 0)
              : (transformedData.balance?.game ?? normalizedOutcome.win);
        }

        // Forward anticipation flag
        const anticipation = transformedData.features?.anticipation ?? transformedData.anticipation;
        if (anticipation !== undefined) {
          normalizedOutcome.anticipation = anticipation;
        }

        if (normalizedOutcome && transformedData.flow?.round_id) {
          normalizedOutcome.id = transformedData.flow.round_id;
        }

        const isRespin = command === "respin";
        let featureState = this.detectFeatureState(transformedData);

        // Bonus game: attach currentRound for trigger/respin
        const isBonusTrigger =
          featureState.type === "bonus" &&
          featureState.phase === "intro" &&
          command === "spin";
        const isBonusRespin =
          featureState.type === "bonus" && command === "respin";

        if (featureState.type === "bonus" && (isBonusTrigger || isBonusRespin)) {
          const originalScreen = rawScreen || data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const normalizedReels = normalizeScreenData(originalScreen);
            featureState.data.currentRound = {
              reels: normalizedReels,
              wins: normalizedOutcome.wins || [],
              win: normalizedOutcome.win || 0,
              bet: normalizedOutcome.bet,
              storage: transformedData.outcome?.storage || null,
            };
          }
        }

        // Read from the same payload as featureState, so a `freespin` reports
        // the feature board instead of falling through to the base game.
        const spinFreeSpinsState = this.detectFreeSpinsState(transformedData);
        const gameMode = this.getGameMode(featureState, spinFreeSpinsState);

        // Balance: trigger spin defers win (added at bonus end)
        const winAmount = normalizedOutcome?.win || 0;
        let finalBalance;
        if (isBonusTrigger) {
          finalBalance = extractedBalance;
        } else {
          finalBalance = extractedBalance + winAmount;
        }

        const adaptedRound = this.adaptSpinRoundToState(normalizedOutcome, bet);

        // ── Round identity ──
        //
        // adaptSpinRoundToState builds a fresh object and does NOT carry an
        // id. Several once-per-round guards downstream compare
        // `currentRound.id` — the SC1 reveal's, and the win display's
        // `${id}-${winsHash}` dedupe. With no id they all compare null to
        // null, which reads as "already done for this round", and the reveal
        // never launched on a live spin at all. It worked in devSimulation
        // only because that round stamps its own id.
        //
        // flow.round_id is the round, and is already trusted as the token.
        // The replay path re-stamps the same way, for the same reason.
        if (adaptedRound) {
          adaptedRound.id = transformedData.flow?.round_id ?? null;
        }

        // ── SC1 landed: the reveal sequence plays — ALWAYS ──
        //
        // The POSITIONS come off the landed screen; what the strip lands on
        // comes from the server. With award evidence it is the package; with
        // none it is the BLANK window (GDD §8) — the same synthetic scatter
        // win, the same doors, the same spin, landing on nothing. Only the
        // TRIGGER — arming the feature, persisting the round — is gated on
        // the evidence, so symbols alone can never start a round.
        // ── A BASE SPIN ENDS THE LAST FREE-SPINS ROUND. Any base spin. ──
        //
        // The record is kept PAST the round's last spin on purpose: the server
        // stops describing a closed round, so a reload has nothing else to
        // replay the outro from. What ends it is the player going back to the
        // base game and spinning — and a BONUS BUY is that spin.
        //
        // Cleared HERE, before anything writes, rather than after — and the
        // order is the whole fix. The clear used to sit two hundred lines below
        // `saveFreeSpinsRound`, so it had to be skipped for a trigger or it
        // would have wiped the record that same spin had just written. That
        // exemption is what let a bought bonus inherit the previous round: the
        // guard was `!fsFeatureLive` (a buy comes back with free spins already
        // live, so it never fired), then `!isFreeSpinTrigger` (a buy IS a
        // trigger, so it never fired either). Two attempts, both aimed at the
        // exemption instead of at the ORDER.
        //
        // With the clear first, no exemption is needed:
        //
        //   plain base spin   clears, nothing rewrites          -> empty
        //   trigger or buy    clears, then saveFreeSpinsRound   -> fresh
        //
        // and saveFreeSpinsRound writes the WHOLE record rather than merging,
        // so a new round cannot inherit a field from the old one by any route.
        if (kindOfCommand(command) === COMMAND_KIND.BASE) {
          // EVERY feature's record, not just free spins. A base spin — plain or
          // a buy — starts a new game, and nothing from the last one can ever
          // be returned to, whichever feature it was. Cleared together and
          // unconditionally, so there is one rule instead of one per feature.
          StorageManager.clearFreeSpinsRound();
          StorageManager.clearBonusGameRound();
        }

        let isFreeSpinTrigger = false;
        let hasRevealSequence = false;
        let gambleScatterReveal = null;
        // Spins a free spin's scatters ADDED to the running round (the
        // retrigger) — `outcome.freespins_issued` is this step's award, the
        // features' one the round's new total. 0 on every other response.
        // const freeSpinsRetrigger =
        //   command === "freespin"
        //     ? Math.max(0, Number(transformedData.outcome?.freespins_issued) || 0)
        //     : 0;
        // ── The SYMBOLS decide it (Vladimir, 2026-10-01: "focus on symbols,
        // that's our main source of truth, and commands") ── a `freespin` with
        // 2+ scatters on the board IS a retrigger (GDD: 2 -> +3, 3 -> +5),
        // whatever the server's numbers say. The count is the server's
        // `freespins_issued` when it sends one, else the GDD's.
        const retriggerScatters =
          command === "freespin"
            ? scanBoardScatters(adaptedRound?.reels ?? transformedData.outcome?.screen)
                .scatters.length
            : 0;
        const freeSpinsRetrigger =
          retriggerScatters >= 2
            ? Math.max(0, Number(transformedData.outcome?.freespins_issued) || 0) ||
              (retriggerScatters >= 3 ? 5 : 3)
            : 0;

        // ── Cleopatra's Fire Hold & Win trigger ──
        //
        // A spin / freespin that entered H&W: light its coins (see
        // withTriggerCoinWin) so the presentation can open the intro. Not for
        // a random pot: its coins are not on the reels (contract §8.3).
        const holdAndWinTrigger = parseHoldAndWinState(transformedData);
        const isHoldAndWinTrigger =
          holdAndWinTrigger?.phase === "trigger" && !holdAndWinTrigger.randomPot;
        if (adaptedRound && isHoldAndWinTrigger) {
          // adaptedRound.wins = withTriggerCoinWin(
          //   adaptedRound.wins,
          //   holdAndWinTrigger.newCells.map((cell) => [cell.reel, cell.row]),
          // );
          // The server's trigger cells, else the coins on the board (symbols).
          // const triggerCoinCells = holdAndWinTrigger.newCells.map((cell) => [
          //   cell.reel,
          //   cell.row,
          // ]);
          const triggerCoinCells = holdAndWinTrigger.newCells.map((cell) => [
            cell.reel,
            toReelRow(cell.row),
          ]);
          adaptedRound.wins = withTriggerCoinWin(
            adaptedRound.wins,
            triggerCoinCells.length ? triggerCoinCells : scanBoardCoins(adaptedRound.reels),
          );
          markJackpotCoins(
            adaptedRound,
            // transformedData.outcome?.storage?.new_cells,
            triggerCellsOnReels(transformedData.outcome?.storage?.new_cells),
            this.jackpots,
          );
        }

        // ── Every coin's number (GDD: every coin shows its value) ──
        // The trigger coins' from the server, the rest cosmetic — decided once
        // here, so the landing picture and its spine show the same number.
        // After markJackpotCoins: a jackpot coin is 16 by now and gets none.
        if (adaptedRound) {
          assignCoinValues(
            adaptedRound,
            // isHoldAndWinTrigger ? transformedData.outcome?.storage?.new_cells : null,
            isHoldAndWinTrigger
              ? triggerCellsOnReels(transformedData.outcome?.storage?.new_cells)
              : null,
            transformedData.outcome?.bet,
            this.baseBet,
          );
        }

        if (adaptedRound) {
          // ── TWO ways in, and only one of them is a picture on the board ──
          //
          // The board scan is checked first and wins outright: when SC1 is
          // standing on reel 4 that is the trigger, and the Golden Mystery is
          // not consulted at all. Only when the board has nothing does the
          // feature get asked, which is the case GDD §7 describes — the cell
          // respun onto the gamble scatter.
          // ── BOTH sources, counted together ──
          //
          // This used to ask the board first and consult the feature only if
          // the board had nothing, which made a trigger split across the two
          // invisible and, when the feature alone completed it, reduced the
          // trigger to the single SC1 cell. See collectAllTriggerPositions.
          const triggerPositions = collectAllTriggerPositions(adaptedRound);
          // The feature's award, asked unconditionally now. It is the THIRD
          // source buildGambleScatterReveal consults, behind the board's own
          // `gamble_scatter_values` and the open ladder, so a board round that
          // also has one is unaffected.
          const goldenAward = goldenGambleTriggerAward(adaptedRound);
          if (triggerPositions.length > 0) {
            // ── The REVEAL belongs to SC1 WHEREVER IT CAME FROM ──
            //
            // This used to read `boardPositions.length > 0`, on the reading
            // that GDD §8 scopes the doors-and-strip to SC1 landing on the
            // board and that a golden cell had already run its own show.
            //
            // That was wrong, and round 1451830 proved it: a board SC, a
            // second SC from the feature, then SC1 from the feature — three
            // scatters, and the third one silent. The cell's own strip settled
            // on `scg`, which is the scatter ARRIVING; what §8 describes is
            // the scatter then OPENING and spinning packages inside itself,
            // and that never ran.
            //
            // So the reveal is owed to any SC1 on screen. The award stays
            // separate below — a blank still opens its doors, spins, lands on
            // the empty window and triggers nothing, exactly as a board blank
            // does.
            // A complete trigger always contains SC1 — that is what makes
            // it complete — so the reveal is owed whenever there is one.
            // No gamble (Cleopatra's Fire) means no reveal.
            hasRevealSequence = isGambleEnabled();
            // The cell IS the evidence in the golden case: `fs_count` is the
            // server stating the award on that cell, which is the same class of
            // statement hasFreeSpinAwardEvidence looks for in `features`.
            //
            // ── A FREE SPIN never triggers free spins ──
            //
            // Three scatters inside the round are a RETRIGGER (Cleopatra GDD
            // §6.1): more spins on the running package, announced by the
            // Additional Free Spins popup. `freespins_issued` is on every free
            // spin, so the evidence test alone read the retrigger as a NEW
            // round — awarded reset to 0, the whole package held back as
            // "unannounced", the button read 0 and the round stalled.
            // isFreeSpinTrigger =
            //   hasFreeSpinAwardEvidence(transformedData.features) ||
            //   !!goldenAward;
            isFreeSpinTrigger =
              command !== "freespin" &&
              (hasFreeSpinAwardEvidence(transformedData.features) ||
                !!goldenAward);
            adaptedRound.wins = withTriggerScatterWin(
              adaptedRound.wins,
              triggerPositions,
            );
            // What SC1's strip must settle on. Carried ON THE ROUND because
            // that is where useWinController reads it from — see the
            // playRevealSequence call, which passes currentRound.gambleScatter
            // straight through to the strip.
            //
            // The golden award is passed too: with no board SC1 there is no
            // `gamble_scatter_values` to read, and the package lives on the
            // cell.
            if (isGambleEnabled()) {
              gambleScatterReveal = buildGambleScatterReveal(
                transformedData.features,
                transformedData.outcome,
                triggerPositions,
                goldenAward,
              );
              adaptedRound.gambleScatter = gambleScatterReveal;
            }
          }

          if (isFreeSpinTrigger) {
            // ── Persist it, because the server stops sending it ──
            //
            // While flow.state is `gamble_freespins` an init still carries the
            // board in `outcome.screen`, so a reload can rebuild the trigger
            // round from the payload. The moment the package is collected and
            // the state becomes `freespins`, `outcome.screen` comes back NULL
            // — verified live — and there is nothing left to rebuild from.
            //
            // Without this the intro phase has no round to replay, which is
            // exactly what a bought round hits: there was no ordinary base
            // spin to save one on the way in.
            //
            // Its own key rather than the base round, because the base round
            // is cleared and stripped on paths that have nothing to do with
            // this feature. Cleared when the round closes — see
            // clearFreeSpinsRound.
            // triggerWin stamped HERE, not inherited: the flag is only
            // computed further down, after this save. A stored trigger round
            // without it replays nothing — the scene effect reads triggerWin
            // to decide whether a win replay exists, and its absence is what
            // once sent a reload straight to the gamble screen with no win
            // sequence.
            // triggerReveal stamped alongside triggerWin, and for the same
            // reason: both are computed below this save, so a stored round
            // would carry neither. Without it a reload replays the trigger
            // with no reveal flag and falls back to the old behaviour —
            // dimming and celebrating before the strip has landed.
            //
            // triggerReveal is the BOARD's flag, so it is stamped from
            // hasRevealSequence rather than hard-coded true: a trigger that
            // came from a Golden Mystery cell has no reveal to replay, and
            // claiming one would leave the reload waiting on a strip that
            // never spins.
            StorageManager.saveFreeSpinsRound({
              triggerRound: {
                ...adaptedRound,
                triggerWin: true,
                triggerReveal: hasRevealSequence,
              },
              bet: adaptedRound.bet ?? normalizedOutcome?.bet ?? bet,
              packageTier: adaptedRound.gambleScatter?.tier ?? null,
            });
          }
        }

        // ── The round, written on EVERY spin ──
        //
        // Pot Collection saves its whole record on each respin
        // (saveBonusGameRound, below) and that is what lets a reload land
        // where the player left off. Free spins kept only the frozen trigger,
        // so a mid-round reload had to re-derive everything from the init and
        // landed on an idle board with no level and no total.
        //
        // Merged, not replaced: the trigger half is frozen and must survive.
        if (command === "freespin") {
          const fsFeatures = transformedData.features || {};
          const fsIssued = fsFeatures.freespins_issued ?? 0;
          const fsLeft = fsFeatures.freespins_left ?? 0;
          const fsLevel = fsFeatures.fs_state?.level ?? 1;
          StorageManager.updateFreeSpinsRound({
            round: adaptedRound || normalizedOutcome,
            bet: adaptedRound?.bet ?? normalizedOutcome?.bet ?? bet,
            awarded: fsIssued,
            played: Math.max(0, fsIssued - fsLeft),
            // The server's running total — our balance.free_rounds.
            totalWin: transformedData.balance?.game ?? 0,
            // The RAW server level, and only that. The room is derived from
            // it by freeSpinsSlice.applyFsState, which owns that rule — the
            // record storing its own idea of the level is how a reload ended
            // up dressed as the entry room.
            serverLevel: fsLevel,
            tier: (fsFeatures.fs_state?.total_collected_scatters ?? 0) % 4,
            // ── Live again: never closed (2026-10-08) ── the merge kept a
            // `closed` stamp through every later spin, so one wrong stamp (the
            // free spin that won Hold & Win, before fsFeatureLive learned its
            // respin state) turned every reload for the rest of the pack into
            // its ending. The closing spin stamps it back below, after this.
            closed: false,
          });
        }

        // ── The trigger win is spent the moment the round starts paying ──
        //
        // Pot Collection strips it on the FIRST respin (see the isFirstRespin
        // branch below) so a reload inside the bonus cannot replay the win
        // that bought it. Free spins never did, so `base_game_round` kept
        // `wins`, `win`, `triggerWin` and `isAnimatedWin` for the whole
        // feature: a reload replayed the base celebration and, once the round
        // closed and gameMode was `base` again, gameSettingsSlice subtracted
        // that same win from displayedBalance.
        //
        // Every free spin rather than only the first, because this feature has
        // no per-spin record to use as PC's `!hasBonusGameRound()`
        // discriminator. The call is idempotent — it early-returns when there
        // is no stored round, and re-stripping an already-stripped one writes
        // the same object — so the only cost is one small write per spin,
        // well under what PC writes per respin.
        if (command === "freespin") {
          StorageManager.stripBaseGameWins();
        }
        // Cleopatra's Fire Hold & Win: the same, on every respin — after the
        // bonus a reload shows the trigger board with NO wins and its idles,
        // never the trigger win again (Vladimir, 2026-09-30).
        if (command === "respin") {
          StorageManager.stripBaseGameWins();
        }

        // ── Drop the stored trigger the moment the feature is over ──
        //
        // One rule for every way out, because there are several and missing
        // any of them leaves a package that no longer exists ready to replay
        // into the NEXT round:
        //
        //   gamble lost   -> flow.state leaves the feature, tier 0, no package
        //   round closed  -> the last freespin returns state "closed"
        //   base spin     -> the player is simply playing the base game again
        //
        // ── ...but NOT on the response that ends it ──
        //
        // The comment above used to claim this mirrored the bonus round, which
        // is cleared "on the first base spin after it ends". It did not: the
        // test was flow.state alone, and the closing `freespin` returns
        // "closed" — so the record was deleted while the total-win dialog was
        // still ahead of the player. A refresh there found nothing and dropped
        // straight to the base game.
        //
        // Pot Collection clears on the next BASE command
        // (`command === "spin" && gameMode === "base"`, further down this
        // method), and that surviving window is exactly what lets it replay a
        // completion phase. Same rule here.
        //
        // The closing response is instead STAMPED into the record, so initGame
        // can rebuild the outro from it the way it rebuilds Pot Collection's.
        const fsFlowState = transformedData.flow?.state;
        // const fsFeatureLive =
        //   fsFlowState === "gamble_freespins" || fsFlowState === "freespins";
        // ── A free spin that wins Hold & Win does NOT end the pack ──
        // (2026-10-08) it returns `state: respin` with the pack's freespins_*
        // beside bonus_data (contract §8.4) — read as "the round is over", it
        // stamped the record CLOSED, and a reload on the bonus's intro opened
        // the free spins' Total Win instead of the trigger and the intro.
        const fsFeatureLive =
          fsFlowState === "gamble_freespins" ||
          fsFlowState === "freespins" ||
          (fsFlowState === "respin" &&
            typeof transformedData.features?.freespins_left === "number");

        // ── A CAPPED round is closed too, whatever flow.state still says ──
        //
        // The cap CANCELS the free spins, and the CLIENT is what acts on it —
        // there is no "end feature" command, so the server keeps the round open
        // and `flow.state` is still `freespins` on the capping response. The
        // test above therefore never fired, the record was never stamped, and
        // the reload had no completion to replay: it fell to the in-progress
        // branch, which raises the "FREE SPINS n LEFT" popup and hands to the
        // loop — and the loop, seeing the cap, opened MAX WIN on the spot. No
        // board, no mysteries, no win replay.
        //
        // Pot Collection never hits this because its cap does NOT end the
        // round: every respin plays out, the server closes it, and the ordinary
        // stamp applies. Ours ends early, so ours has to say so itself.
        const fsCapped =
          command === "freespin" &&
          detectWinLimitReached(
            transformedData,
            transformedData.balance?.game ?? 0,
          );

        if ((!fsFeatureLive || fsCapped) && command === "freespin") {
          // The closing spin: stamp the record CLOSED and leave it. The outro
          // is still owed, and until the player spins the base game again this
          // record is the only thing that knows the total.
          StorageManager.updateFreeSpinsRound({
            closed: true,
            totalWin: transformedData.balance?.game ?? 0,
            awarded: transformedData.features?.freespins_issued ?? 0,
            played: transformedData.features?.freespins_issued ?? 0,
            // ── Stamped, because a completion reload cannot recompute it ──
            //
            // Everything else about the cap is derived from the live response,
            // which is gone by the time the outro is replayed: the API has
            // already returned to base. Without this the reload always
            // answered "not capped" and opened TOTAL WIN over a round that had
            // just hit MAX WIN.
            //
            // Taken from the closing response, so it carries the SERVER's own
            // win_limit_reached and not only our client-side threshold.
            winLimitReached: detectWinLimitReached(
              transformedData,
              transformedData.balance?.game ?? 0,
            ),
          });
        }
        // ── The pack can also END on a RESPIN (2026-10-19) ── Hold & Win won on
        // the LAST free spin: its settling respin closes the bonus and the
        // free spins together (in a campaign, the campaign too). The stamp
        // above only reads `freespin`, so the record stayed open, and a reload
        // restored the bonus's collection (its own record) but not the free
        // spins' ending — the exit went straight to base, no Total Win. A
        // record that exists here belongs to THIS pack (a base spin clears it
        // first); `fsFeatureLive` false means the server has left the pack.
        if (
          command === "respin" &&
          parseHoldAndWinState(transformedData)?.phase === "settled" &&
          // !fsFeatureLive
          // The server has left the pack, OR it reports none left — either
          // way this settling respin was the pack's end.
          (!fsFeatureLive || (transformedData.features?.freespins_left ?? 0) === 0)
        ) {
          const openPack = StorageManager.loadFreeSpinsRound();
          if (openPack && openPack.closed !== true && (openPack.awarded ?? 0) > 0) {
            const issued = transformedData.features?.freespins_issued ?? openPack.awarded;
            StorageManager.updateFreeSpinsRound({
              closed: true,
              // The round's whole total, the bonus's payout included — the
              // H&W exit knows it is in (holdAndWinSlice paidInFreeSpinsTotal).
              totalWin: transformedData.balance?.game ?? 0,
              awarded: issued,
              played: issued,
              winLimitReached: detectWinLimitReached(
                transformedData,
                transformedData.balance?.game ?? 0,
              ),
            });
          }
        }
        // The base-game clear that used to live here has moved to the TOP of
        // this handler, before anything writes — see the note there. Leaving it
        // here meant it could only ever run for a spin that did NOT trigger,
        // which is precisely the case that did not need it.

        // triggerWin: single flag for useWinController — true when there's any
        // win or a feature trigger. A trigger spin commonly pays 0 (free spins
        // pay out later), so `win > 0` alone would miss it entirely.
        // hasRevealSequence: a blank reveal wins nothing and triggers nothing,
        // but its presentation must still run — same show, empty landing.
        if (adaptedRound) {
          // ── A two-scatter retrigger has no scatter win to play ──
          // It pays nothing (GDD: +3, no scatter cash win; contract §8.1
          // "2 scatters → +3, win 0"), so `wins` carries no scatter entry —
          // and the Additional Free Spins popup opens off the scatter LINE
          // playing (useFreeSpinBasicWinController, retriggerWinShown). With
          // no line the round waited for ever (2026-10-01). The same 0-pay
          // line the trigger spin gets (withTriggerScatterWin), from the
          // scatters on the board; a 3-scatter retrigger keeps the server's.
          if (freeSpinsRetrigger > 0) {
            adaptedRound.wins = withTriggerScatterWin(
              adaptedRound.wins,
              scanBoardScatters(adaptedRound.reels).scatters,
            );
          }
          adaptedRound.triggerWin =
            adaptedRound.win > 0 ||
            isBonusTrigger ||
            isFreeSpinTrigger ||
            // A retrigger's scatters play their win even on a 0 pay.
            freeSpinsRetrigger > 0 ||
            isHoldAndWinTrigger ||
            hasRevealSequence;
          // What the Additional Free Spins popup announces — see
          // useFreeSpinBasicWinController's retrigger branch.
          adaptedRound.freeSpinsRetrigger = freeSpinsRetrigger;

          // ── triggerReveal: the SHOW, separated from the WIN ──
          //
          // `triggerWin` above is one boolean carrying four different
          // situations, so useWinController could not tell a real win from a
          // reveal whose outcome is not known yet — and therefore dimmed the
          // board and celebrated the moment three scatters landed, before the
          // strip had decided anything.
          //
          // This flag is the presentation half on its own: TRUE whenever SC1
          // is on the board, whatever it goes on to land on. The award half
          // stays where it already is — `freeSpinsTrigger` is emitted only
          // when hasFreeSpinAwardEvidence passed, so `freeSpins.triggered`
          // is the "a package was actually won" signal and is false on a
          // blank.
          //
          // Read together: triggerReveal says "hold the UI and run the
          // reveal, do not dim yet", and freeSpins.triggered says "it paid —
          // now dim and celebrate".
          adaptedRound.triggerReveal = hasRevealSequence;
        }

        // ── A FREE-SPINS trigger defers its win too (QA, 2026-10-28) ──
        // Every bonus works the same way: the trigger spin's win is carried
        // INTO the bonus (freeSpins.totalWin is seeded with it) and reaches the
        // balance with the bonus's winnings at the end, when the exit syncs the
        // balance (TransitionDialog). The deferral above only knows the Hold &
        // Win trigger — isFreeSpinTrigger is decided further down — so a paying
        // FS trigger moved the balance on entry and the win showed in Total Win
        // AND balance through the whole round.
        if (isFreeSpinTrigger) {
          finalBalance = extractedBalance;
        }

        const gameState = {
          balance: finalBalance,
          bet: transformedData.outcome?.bet || null,
          round: adaptedRound || normalizedOutcome,
          gameMode,
          featureState,
        };

        // ── The base round must survive the whole free-spins feature ──
        //
        // detectFeatureState only knows the respin bonus, so every free-spins
        // command lands here as gameMode "base" and would be written over
        // base_game_round: the gamble with its EMPTY outcome (screen null —
        // that wiped the trigger screen), a freespin with the feature's own
        // board. Only a BASE command may save — the trigger spin and the buy
        // (both command "spin") do, because their board is the base screen
        // the player comes back to, exactly as a Pot Collection trigger does.
        //
        // A DECISION saves no round either, but it does own one piece of the
        // stored ones: the gamble data. Patched in place, immediately, the
        // same merge gameSettingsSlice does for the round on screen.
        const commandKind = kindOfCommand(command);
        if (commandKind === COMMAND_KIND.BASE) {
          this.saveGameState(gameState);
        } else if (commandKind === COMMAND_KIND.DECISION) {
          this._patchStoredGambleData(transformedData.features);
        }

        // ── Trigger spin: save triggerWin immediately ──
        // Capture the base game win that triggered the bonus BEFORE any respins.
        // This is frozen for the entire bonus — respins never overwrite it.
        if (isBonusTrigger) {
          const baseTriggerWin = adaptedRound?.win ?? 0;
          StorageManager.saveTriggerWin(baseTriggerWin);
        }

        // Save bonus game round to localStorage on each respin
        if (isBonusRespin && featureState.data?.currentRound) {
          // Safety: only strip + save if this is a confirmed valid bonus respin response.
          // Requires phase=in_progress, a non-empty reels grid, and respinsLeft defined.
          // This prevents accidental stripping on malformed/partial responses.
          const isValidBonusRespinResponse =
            featureState.phase === "in_progress" &&
            Array.isArray(featureState.data.currentRound.reels) &&
            featureState.data.currentRound.reels.length > 0 &&
            featureState.data.respinsLeft !== undefined;

          if (isValidBonusRespinResponse) {
            const isFirstRespin = !StorageManager.hasBonusGameRound();
            // On first respin, strip wins from base game round so reload won't replay win animations
            if (isFirstRespin) {
              StorageManager.stripBaseGameWins();
            }
            const respinsLeft = featureState.data?.respinsLeft ?? 0;
            StorageManager.saveBonusGameRound({
              round: featureState.data.currentRound,
              bet: gameState.bet,
              respinsLeft,
              respinsIssued: featureState.data?.respinsIssued ?? 3,
              // totalWin: only needed for completion reload — set on last respin only
              totalWin: respinsLeft === 0 ? (gameState.round?.win ?? 0) : 0,
              coinCount: featureState.data?.coinCount ?? 0,
              respinGameType: featureState.data?.respinGameType || "",
              initialRespinScreen: featureState.data?.initialRespinScreen || null,
              initialRespinValues: featureState.data?.initialRespinValues || null,
              initialRespinOriginalValues: featureState.data?.initialRespinOriginalValues || null,
              initialEvents: featureState.data?.initialEvents || [],
              triggerCoordinates: featureState.data?.triggerCoordinates || null,
              valuesGrid: featureState.data?.valuesGrid || null,
              originalValues: featureState.data?.originalValues || null,
              prevScreen: featureState.data?.prevScreen || null,
              screenProgress: featureState.data?.screenProgress || [],
              // Accumulate flippedCoinPositions: load previous + process current events
              flippedCoinPositions: this._accumulateFlippedPositions(featureState),
              // Win limit flag — BE source of truth
              winLimitReached: featureState.data?.winLimitReached || false,
            });
          }
        }
        // NOTE: stripBaseGameWins() is intentionally NOT called here on isBonusTrigger.
        // Until the first actual respin is saved, every reload must land on intro phase
        // and replay the trigger spin win animation. The strip happens on the FIRST respin
        // above (inside the isBonusRespin block, guarded by !hasBonusGameRound()).

        if (command === "spin" && gameMode === "base") {
          // Always clear bonus game round on base game spin
          StorageManager.clearBonusGameRound();
          // Only clear free rounds data when FR is finished —
          // check localStorage since API response format may vary
          const currentFR = StorageManager.loadFreeRoundsRound();
          if (!currentFR || currentFR.status !== "active") {
            StorageManager.clearFreeRoundsRound();
          }
        }

        // ── Free Rounds from spin response ──
        const freeRoundsData = transformedData.free_rounds || null;
        const freeRoundsTotalWin = transformedData.balance?.free_rounds ?? null;

        // Save FR state to localStorage on each FR spin (for reload recovery)
        if (freeRoundsData) {
          if (StorageManager.hasFreeRoundsRound()) {
            // Exists → only update changing fields, never touch betToRestore
            StorageManager.saveFreeRoundsRound({
              status: freeRoundsData.status,
              betValue: freeRoundsData.bet_value,
              performed: freeRoundsData.performed,
              count: freeRoundsData.count,
              win: freeRoundsTotalWin ?? StorageManager.loadFreeRoundsRound()?.win ?? 0,
              // This round's own win is NOT folded into `win` yet — that only
              // happens once its reveal plays (WinDisplay → updateFreeRoundsWin).
              // Set immediately, synchronously, so a reload right here already
              // knows the persisted total is pre-reveal.
              lastRoundApplied: false,
            });
          } else {
            // Doesn't exist → create with betToRestore from base_game_round
            StorageManager.saveFreeRoundsRound({
              status: freeRoundsData.status,
              betValue: freeRoundsData.bet_value,
              performed: freeRoundsData.performed,
              count: freeRoundsData.count,
              win: freeRoundsTotalWin ?? 0,
              betToRestore: StorageManager.loadBaseGameRound()?.bet ?? null,
              lastRoundApplied: false,
            });
            // Strip stale base game wins so reload at FR round 0 won't replay
            // the previous base game win animation. Bet is already captured above.
            StorageManager.stripBaseGameWins();
          }
        }

        const result = {
          token: transformedData.flow?.round_id || null,
          bet: gameState.bet,
          balance: finalBalance,
          round: adaptedRound || normalizedOutcome,
          gameMode,
          featureState,
          // ── The same shape the init path carries ──
          //
          // Pot Collection's featureState is on BOTH results, so its slice
          // reads one shape wherever the data came from. Free spins carried
          // this on the init only, so the live path re-parsed the raw
          // `features.*` keys and recomputed rules the provider had already
          // applied — two owners of one rule, and the reason nothing outside
          // freeSpinsSlice could tell the feature was running.
          //
          // Already computed above for getGameMode; this only publishes it.
          freeSpinsState: spinFreeSpinsState,
          availableActions: transformedData.flow?.available_actions || ["init", "spin"],
          flowState: transformedData.flow?.state || "ready",
          features: transformedData.features || null,
          // Cleopatra's Hold & Win, parsed from features.bonus_data — see
          // holdAndWinState.js. Null when the round has no H&W.
          holdAndWin: parseHoldAndWinState(transformedData),
          // Parsed here rather than at the call site so it arrives the same way
          // from every command that can carry it — a buy, a trigger spin, or
          // one of the two gamble actions. Null when the round has no gamble.
          gamble: parseGambleState(transformedData.features),
          // ── The free-spins trigger ──
          // Present ONLY on the spin that triggered, so freeSpinsSlice can
          // raise `triggered` and record the package without having to guess
          // from the board. Undefined on every other response, which is what
          // stops a later spin from re-arming a round already in progress.
          freeSpinsTrigger: isFreeSpinTrigger
            ? {
                spins: gambleScatterReveal?.value ?? 0,
                tier: gambleScatterReveal?.tier ?? null,
                reveal: gambleScatterReveal,
              }
            : undefined,
          freeRounds: freeRoundsData,
          freeRoundsTotalWin,
          command,
          progress: transformedData.progress || null,
        };

        // Attach raw respin round for bonus grid rendering
        if (isRespin) {
          const originalScreen = rawScreen || data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const respinRound = { ...normalizedOutcome };
            respinRound.reels = normalizeScreenData(originalScreen);
            result.respinRound = respinRound;
          }
        }
        // ── FR cancellation on any spin/respin ──
        // Server indicates FR was revoked. Reject with 206 immediately
        // to trigger error recovery overlay and reset the game cleanly.
        if (freeRoundsData?.status === "canceled") {
          StorageManager.clearFreeRoundsRound();
          StorageManager.clearBonusGameRound();
          reject({
            errors: [{ code: 206, desc: "free_rounds_canceled" }],
          });
          return;
        }

        // ── Cleopatra's Fire Random Pot: kept for the presentation spin ──
        // (randomPotPresentation below). Only the trigger step; anything else
        // lets it go.
        this._randomPotTrigger =
          result.holdAndWin?.phase === "trigger" && result.holdAndWin?.randomPot
            ? {
                result,
                // rawNewCells: transformedData.outcome?.storage?.new_cells || [],
                rawNewCells:
                  triggerCellsOnReels(transformedData.outcome?.storage?.new_cells) || [],
                bet: transformedData.outcome?.bet,
              }
            : null;

        // ── Cleopatra's Fire Hold & Win: the settling step, kept ── a reload
        // replays the collection from it (init fallback above).
        // if (result.holdAndWin?.phase === "settled") {
        //   StorageManager.saveHoldAndWinSettled({ holdAndWin: result.holdAndWin });
        // } else if (result.holdAndWin?.phase === "trigger") {
        //   StorageManager.clearHoldAndWinSettled();
        // }
        // Kept until the NEXT play command (spin, freespin, a buy) — Pot
        // Collection's bonus_game_round rule (cleared on the next base spin)
        // and free spins' completion record: a reload any time after the end
        // replays it. Clearing it at the room's exit lost it (Vladimir,
        // 2026-09-30: the outro reload landed on the plain base game).
        if (result.holdAndWin?.phase === "settled") {
          // StorageManager.saveHoldAndWinSettled({ holdAndWin: result.holdAndWin });
          // With the stake it was played at — a reload after the player
          // changed the bet still shows the bonus at its own (initGame).
          StorageManager.saveHoldAndWinSettled({
            holdAndWin: result.holdAndWin,
            bet: transformedData.outcome?.bet || null,
          });
        } else if (command !== "respin") {
          StorageManager.clearHoldAndWinSettled();
        }
        // ── The trigger win, frozen at the trigger ── Pot Collection's
        // saveTriggerWin: the base board's wins are stripped on the respins
        // (below), so TOTAL WIN reads it from here after a reload.
        if (result.holdAndWin?.phase === "trigger") {
          StorageManager.saveTriggerWin(result.round?.win ?? 0);
        }

        resolve(result);
      };

      this.worker.onerror = (event) => {
        // ── A worker that has errored is DEAD, and it was being kept ──
        //
        // `this.worker` is created once and reused for the session. Rejecting
        // without clearing it left the corpse assigned: every later spin posted
        // into a worker whose `onmessage` had never been defined, so the
        // promise simply never settled and the game was finished until reload —
        // even after whatever broke had recovered.
        //
        // Dropped here so the next spin builds a fresh one. That is what makes
        // a transient failure transient.
        const dead = this.worker;
        this.worker = null;
        try {
          dead?.terminate();
        } catch {
          // Already gone. Nothing to release.
        }
        reject(new Error(`PopiPlay: Worker error: ${event.message || "Unknown worker error"}`));
      };

      const tokenToUse = token || this.currentToken || playToken;
      const purchasedFeatureLevel = spinOptions?.purchased_feature_level || null;

      this.worker.postMessage({
        bet,
        token: tokenToUse,
        apiUrl,
        csrfTokenName: csrfTokenHeaderName,
        csrfTokenValue: csrfTokenHeaderValue,
        identifier,
        command,
        ...(purchasedFeature ? { purchasedFeature } : {}),
        ...(purchasedFeatureLevel ? { purchasedFeatureLevel } : {}),
        ...(spinOptions ? { options: spinOptions } : {}),
      });
    });
  }

  // ── Spin Params ──

  getSpinParams({ bet, featureState, freeRoundsActive, freeRoundsBet, purchasedFeature, purchasedLevel }) {
    if (featureState?.type === "bonus") {
      return { command: "respin", spinOptions: undefined };
    }

    // Free rounds: regular spin command with bonus flag and FR bet (same as Dmon)
    if (freeRoundsActive) {
      return { command: "spin", spinOptions: { bonus: true, bet: freeRoundsBet || bet } };
    }

    // Bonus buy: include level so the API knows which tier
    if (purchasedFeature && purchasedLevel) {
      const spinOpts = { purchased_feature_level: String(purchasedLevel) };

      const { DEV_FEATURE_COMBO } = require("../../../devSimulation");
      if (DEV_FEATURE_COMBO) {
        spinOpts.feature_combo = DEV_FEATURE_COMBO;
      }

      return {
        command: "spin",
        spinOptions: spinOpts,
      };
    }

    return { command: "spin", spinOptions: undefined };
  }

  // ── Buy Feature ──

  // Level "1" → random features (80× bet), level "2" → all 3 DMC (300× bet)
  async buyFeature(bet, level = "1") {
    const state = (await import("../../../State/Store/store")).default.getState();
    const token = state.game.token;
    return this.spin(bet, token, "spin", "bonus_buy", {
      purchased_feature_level: String(level),
    });
  }

  // ── Free-spin gamble ──

  /**
   * Send one gamble action.
   *
   * Routed through `spin()` for the same reason `buyFeature` is: the command
   * is just a string in the request body, and everything after it — the
   * worker, error normalisation, balance, `flow.available_actions` — is
   * identical whatever was sent. Duplicating that pipe for two more commands
   * would be two more places to fix the next time the transport changes.
   *
   * These responses carry `outcome.screen: null`, which the transform already
   * guards for, so no reels are touched. What the caller wants is
   * `result.gamble`.
   *
   * GUARDED on `flow.available_actions`. The server rejects an action it did
   * not offer, and on this operator a rejected action on a closed round is
   * what got Zeus players banned — so it is never sent on a guess.
   *
   * @param {"gamble_freespins"|"collect_freespins"} action
   */
  async gambleAction(action) {
    const validActions = Object.values(GAMBLE_ACTIONS);
    if (!validActions.includes(action)) {
      throw new Error(`PopiPlay: unknown gamble action "${action}"`);
    }

    const state = (await import("../../../State/Store/store")).default.getState();
    const available = state.game?.availableActions || [];
    if (!available.includes(action)) {
      // Not an exception: the UI can legitimately ask a moment after the round
      // moved on. Reported in the API's OWN error shape — 202 is the code the
      // server itself returns for this — so the caller handles it exactly the
      // same way it handles a real refusal. spin() rejects with this shape
      // throughout, and spinSlice branches on `error.errors`, so an Error
      // subclass here would take a different path for no gain.
      // eslint-disable-next-line no-throw-literal
      throw {
        errors: [{ code: 202, desc: "unavailable_action" }],
      };
    }

    return this.spin(state.game.bet, state.game.token, action, undefined, undefined);
  }

  /** Risk the current package for a better tier. */
  gambleFreespins() {
    return this.gambleAction(GAMBLE_ACTIONS.GAMBLE);
  }

  /** Keep the current package and start the free spins. */
  collectFreespins() {
    return this.gambleAction(GAMBLE_ACTIONS.COLLECT);
  }

  async simulate(slotface, bet, token) {
    throw new Error("simulate() must be implemented");
  }

  // ── Balance Strategy ──

  getBalanceStrategy() {
    // Replay (docs/replay-history-spec.md §9, Stage 3): don't touch the live
    // wallet at all — gameSettingsSlice's bet-deduction and balance-merge
    // (rgsPlay.fulfilled) both gate on these flags, so this alone keeps a
    // replay spin from deducting a real bet or merging its win into the
    // real balance, with no changes needed on the slice side.
    if (this.replayMode) {
      return {
        serverIncludesWin: false,
        clientDeductsBet: false,
        clientAddsWin: false,
      };
    }
    return {
      serverIncludesWin: false,
      clientDeductsBet: true,
      clientAddsWin: true,
    };
  }

  shouldSyncBalanceOnWinClose() {
    return true;
  }

  // ── Session Management ──


  getCurrentToken() {
    return this.currentToken || null;
  }

  // ── Feature Detection ──

  /**
   * Detect free-spins state from an API response — the sibling of
   * detectFeatureState, built the same way for the same reason.
   *
   * Pot Collection has to work out its phase from an indirect signal:
   * `outcome.storage` present on an init means respins were already played.
   * Free spins do not need that guesswork, because the server names the phase
   * outright. `flow.state` IS the state machine (contract §1):
   *
   *   gamble_freespins   package won, player still owes a decision
   *   freespins          package taken, the round is live
   *   closed / ready     no feature — base game
   *
   * So the only thing left to derive is intro-vs-mid inside `freespins`, and
   * the counters answer that with no ambiguity: nothing has been played while
   * `freespins_left` still equals `freespins_issued`.
   *
   * The phase names deliberately match Pot Collection's, so a reader who
   * knows one knows the other.
   *
   *   "intro"       the round has not started — the whole entry replays:
   *                 board, symbol sequence, win, then the GAMBLE SCREEN,
   *                 which is the one gate into the feature. Whether the
   *                 ladder is still open or already collected is DATA
   *                 (`gambleSettled`), not a phase: the screen shows its
   *                 state and, with nothing left to offer, hands straight
   *                 over to the intro popup by its own callback.
   *   "in_progress" mid-round
   *
   * @returns {{phase: string, data: object}|null} null when the response
   *   carries no free-spins round, so a caller can tell that apart from a
   *   round that happens to be at level 0.
   */
  // detectFreeSpinsState(apiResponse) {
  detectFreeSpinsState(apiResponse, { init = false } = {}) {
    const { flow, features } = apiResponse || {};
    const state = flow?.state;

    // ── A Hold & Win won INSIDE free spins, reloaded (2026-10-08) ──
    // Vladimir: "we have to restore both things as they were". The init is the
    // last play response (contract §9): `state: respin` while the bonus runs
    // — with the pack's freespins_* beside bonus_data (§8.4) — so the pack
    // read as no free spins at all and the bonus handed back to the BASE game.
    // Init only: a live trigger free spin is the free-spins room's own spin
    // and is read as before.
    const holdAndWinInsidePack =
      init &&
      state === "respin" &&
      typeof features?.freespins_left === "number" &&
      !!features?.bonus_data;
    // A bonus's grid on this init (running, or settled and still in the
    // payload until the next free spin — §8.4): the pack sits UNDER it. Its
    // board comes back without a win (the respin's outcome repeats the
    // trigger free spin's board; its win is the bonus's), and the room is not
    // parked — the bonus owns the screen, and the pack carries on when it
    // hands back, as live.
    const holdAndWinOwns = init && !!features?.bonus_data;

    // if (state !== "gamble_freespins" && state !== "freespins") return null;
    if (
      state !== "gamble_freespins" &&
      state !== "freespins" &&
      !holdAndWinInsidePack
    ) {
      return null;
    }

    const gamble = parseGambleState(features);

    if (state === "gamble_freespins") {
      // No ladder means no package, and a gamble screen with nothing behind it
      // would ask the player to bet something they do not hold.
      if (!gamble?.current) return null;
      return {
        phase: "intro",
        data: {
          gamble,
          gambleSettled: false,
          fsState: null,
          // The trigger spin's own win. No free spin has been played yet, so
          // `balance.game` holds nothing else — captured live on round 1426501
          // as outcome.win 2 / balance.game 2 with the ladder still open.
          //
          // This is what lets a refresh here rebuild the carried win with NO
          // localStorage, which is the whole reason it is read from the server
          // rather than a storage key the way Pot Collection has to.
          totalWin: capRoundTotal(apiResponse, apiResponse?.balance?.game || 0),
          // A trigger spin alone can in principle reach the cap — cheap to
          // answer here rather than leave one branch blind.
          winLimitReached: detectWinLimitReached(
            apiResponse,
            apiResponse?.balance?.game || 0,
          ),
        },
      };
    }

    const issued = features?.freespins_issued;
    const left = features?.freespins_left;
    const played =
      typeof issued === "number" && typeof left === "number"
        ? Math.max(0, issued - left)
        : 0;

    // ── The board, mid-round, straight off the init ──
    //
    // `outcome.screen` IS sent once free spins have actually been played — it is
    // only null in the 0-played window, which is the case the stored trigger
    // round exists for. So a part-played round needs NO localStorage at all: the
    // last screen, its storage and the running total are all here. Deleting
    // local storage must not stop the round coming back, exactly as it does not
    // for Pot Collection.
    //
    // ── The WIN comes back with the board ──
    //
    // This used to send `wins: []` and `win: 0` on the argument that "the
    // player already watched that spin pay; the screen is being restored, not
    // replayed". Both halves of that were wrong.
    //
    // It is not a restore. `storage` is on this object, and `storage`
    // is what the mystery reveal and the Golden Mystery loop replay FROM — so
    // the reload already replayed the whole feature: the doors, the coins, the
    // multiplier, the collectors. With no `win` behind it, the win controller
    // returned at `!currentRound?.triggerWin` and the round simply stopped
    // there: the feature played out and then nothing paid, nothing counted and
    // no line cycled. Reported from a Golden Mystery with coins.
    //
    // And the player may not have watched it. A reload during the presentation
    // — or during the feature that precedes it — is exactly when this record is
    // read.
    //
    // Re-presenting is safe because free spins is idempotent about it:
    // `freeSpins.totalWin` is ASSIGNED from the server's running total, never
    // accumulated, and syncFreeSpinsDisplayedWin assigns rather than adds (see
    // WinDisplay), so a re-presented win re-snaps to the number it already had.
    // The balance is not touched at all mid-round — `isFreeSpinsMode` is in
    // shouldSkipBalanceUpdate.
    //
    // Built with the same four derived fields a live spin gets, because the
    // consumer is the same controller: `winType` and `isAnimatedWin` decide the
    // celebration tier, `triggerWin` is the flag it gates on, and `id` is what
    // it dedupes by — a round with no id compares null to null and reads as
    // already presented. See adaptSpinRoundToState, which this mirrors.
    const outcome = apiResponse?.outcome;
    // const lastRound =
    //   played > 0 && Array.isArray(outcome?.screen)
    const builtLastRound =
      played > 0 && Array.isArray(outcome?.screen)
        ? (() => {
            const win = outcome.win || 0;
            const winType = this.computeWinType(win, outcome.bet);
            return {
              reels: normalizeScreenData(outcome.screen),
              // ── PARSED, not raw ──
              //
              // `outcome.wins` is the server's tuple form —
              // `["coins", 60, [[0,0],[2,5]]]` for a Golden Mystery payout, and
              // `[lineId, amount, rows, ...]` for a line. Handed over raw,
              // matchHorizontalWinningLines destructures `positions` off an
              // ARRAY, gets undefined, hits its `!Array.isArray(positions)`
              // guard and skips every entry — so the round reached
              // "-> presenting" with one win and then matched nothing, which is
              // the same silence as having no win at all.
              //
              // normalizeWins is the one parser for this, and every other path
              // already uses it (the live spin at `transformedOutcome.wins`, and
              // the base-game trigger reconstruction). It wants the RAW screen:
              // it maps symbol ids itself.
              wins: this.normalizeWins(outcome.wins, outcome.screen),
              win,
              winType,
              isAnimatedWin:
                winType !== "" &&
                PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
              // `win > 0` is the whole test here. The other three cases the live
              // rule covers — a bonus trigger, a free-spins trigger, a reveal
              // sequence — cannot occur on a part-played free spin.
              triggerWin: win > 0,
              id: flow?.round_id ?? null,
              bet: outcome.bet ?? null,
              storage: outcome.storage || null,
            };
          })()
        : null;
    // Under a bonus: the board only (see holdAndWinOwns).
    const lastRound =
      builtLastRound && holdAndWinOwns
        ? { ...builtLastRound, wins: [], win: 0, triggerWin: false }
        : builtLastRound;

    return {
      // phase: played === 0 ? "intro" : "in_progress",
      // Under a bonus the pack is always mid-round: the free spin that won it
      // was played.
      phase: played === 0 && !holdAndWinOwns ? "intro" : "in_progress",
      data: {
        gamble,
        gambleSettled: true,
        holdAndWinOwns,
        issued: typeof issued === "number" ? issued : 0,
        left: typeof left === "number" ? left : 0,
        played,
        lastRound,
        // Untouched. freeSpinsSlice already knows how to read fs_state for a
        // live spin — level is 1-indexed, collector is scatters % 4 — and one
        // reader is better than two that can drift.
        fsState: features?.fs_state || null,
        // The round's running total. Pot Collection has to keep this in
        // localStorage; the server carries ours (contract §8).
        totalWin: capRoundTotal(apiResponse, apiResponse?.balance?.game || 0),
        // The win cap. Detected HERE so live play and reload cannot disagree —
        // both paths come through this method.
        winLimitReached: detectWinLimitReached(
          apiResponse,
          apiResponse?.balance?.game || 0,
        ),
      },
    };
  }

  // Detect bonus game state from API response.
  // Discriminator: features.respin_game_type present + flow.state === "respin"
  // Phase detection:
  //   "intro"       — bonus triggered, no respins played yet (storage is null)
  //   "in_progress" — mid-bonus, respins already played (storage has data)
  //   "outro"       — completion phase (future)
  detectFeatureState(apiResponse) {
    const { flow, features } = apiResponse;

    if (
      features?.respins_left !== undefined &&
      features?.respin_game_type &&
      (flow?.state === "respin" || features.trigger_screen !== undefined)
    ) {
      // Phase detection:
      //   spin command  → always intro (trigger spin from base game)
      //   respin command → always in_progress (actively playing bonus)
      //   init command   → check outcome.storage: null = intro, present = in_progress
      let phase;
      if (flow?.command === "spin") {
        phase = "intro";
      } else if (flow?.command === "respin") {
        phase = "in_progress";
      } else if (flow?.command === "init" && flow?.state === "respin") {
        phase = apiResponse.outcome?.storage ? "in_progress" : "intro";
      } else {
        phase = "intro";
      }

      const storage = apiResponse.outcome?.storage || {};

      // Fix raw multiplier values → actual win amounts using outcome.wins
      const outcomeWins = apiResponse.outcome?.wins || [];
      if (outcomeWins.length > 0) {
        const winLookup = {};
        for (const win of outcomeWins) {
          if (win[0] === "coin" && win.length >= 4) {
            winLookup[`${win[2]}_${win[3]}`] = win[1];
          }
        }

        if (storage.values && Array.isArray(storage.values)) {
          for (let ri = 0; ri < storage.values.length; ri++) {
            if (!Array.isArray(storage.values[ri])) continue;
            for (let si = 0; si < storage.values[ri].length; si++) {
              const key = `${ri}_${si}`;
              if (winLookup[key] !== undefined) {
                storage.values[ri][si] = winLookup[key];
              }
            }
          }
        }

        if (features.initial_respin_values && Array.isArray(features.initial_respin_values)) {
          for (let ri = 0; ri < features.initial_respin_values.length; ri++) {
            if (!Array.isArray(features.initial_respin_values[ri])) continue;
            for (let si = 0; si < features.initial_respin_values[ri].length; si++) {
              const key = `${ri}_${si}`;
              if (winLookup[key] !== undefined) {
                features.initial_respin_values[ri][si] = winLookup[key];
              }
            }
          }
        }
      }

      // Detect new coins: diff prev_screen vs outcome.screen (blank → non-blank)
      let newCoins = [];
      if (phase === "in_progress") {
        const prevScreen = storage.prev_screen || normalizeScreenData(features.initial_respin_screen) || [];
        const currentScreen = apiResponse.outcome?.screen || [];
        for (let ri = 0; ri < currentScreen.length; ri++) {
          if (!Array.isArray(currentScreen[ri])) continue;
          for (let si = 0; si < currentScreen[ri].length; si++) {
            const prevId = parseInt(prevScreen?.[ri]?.[si], 10) || 0;
            const currId = parseInt(currentScreen[ri][si], 10) || 0;
            if (prevId === BLANK_SYMBOL_ID && currId !== BLANK_SYMBOL_ID) {
              let zeusFeature = null;
              if (currId === ZEUS_MYSTERY_ID || currId === CM_MINOR_ID || currId === CM_MINI_ID || currId === CM_MAJOR_ID) {
                zeusFeature = "M";
              } else if (currId === ZEUS_DOUBLE_ID || currId === CD_MULT_ID) {
                zeusFeature = "D";
              } else if (currId === ZEUS_COLLECT_ID) {
                zeusFeature = "C";
              }
              newCoins.push({
                reelIndex: ri,
                symbolIndex: si,
                symbolId: currId,
                isZeus: zeusFeature !== null,
                zeusFeature,
              });
            }
          }
        }
      }

      // Build events list (new API: events/initial_events, legacy: screen_progress)
      const rawScreenProgress = phase === "intro"
        ? [...(features.initial_events || features.initial_screen_progress || []), ...(storage.events || storage.screen_progress || [])]
        : (storage.events || storage.screen_progress || []);

      return {
        type: "bonus",
        phase,
        data: {
          respinsLeft: features.respins_left,
          respinsIssued: features.respins_issued ?? 3,
          respinGameType: features.respin_game_type,
          triggerScreen: features.trigger_screen,
          triggerCoordinates: features.trigger_coordinates || null,
          initialRespinScreen: normalizeScreenData(features.initial_respin_screen) || null,
          initialRespinValues: features.initial_respin_values || null,
          screenProgress: this._normalizeScreenProgress(rawScreenProgress),
          coinValues: storage.coin_values || null,
          prevScreen: storage.prev_screen || null,
          valuesGrid: storage.values || null,
          originalValues: storage.original_values || null,
          initialRespinOriginalValues: features.initial_respin_original_values || null,
          newCoins,
          // Coin count: use API field when available, fallback to counting non-blanks
          // initial_respin_screen has zeus coins (non-16) — use on trigger/init
          // outcome.screen is the bonus grid only on respin (on trigger/init it's the base game screen)
          coinCount: storage.coins_count
            ?? features.initial_coins_count
            ?? features.coin_count
            ?? 0,
          winLimitReached: (() => {
            if (storage.win_limit_reached) return true;
            const totalWin = apiResponse.balance?.game || 0;
            const betAmount = apiResponse.outcome?.bet || 0;
            const maxMultiplier = FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER || 10000;
            return betAmount > 0 && totalWin >= betAmount * maxMultiplier;
          })(),
          currentRound: apiResponse.outcome ? {
            reels: apiResponse.outcome.screen || [],
            wins: apiResponse.outcome.wins || [],
            win: apiResponse.balance?.game || 0,
            storage,
          } : null,
          // The activation event log for the trigger spin — used to determine which mystery
          // coins were actually revealed (m_reveals) during the initial activation sequence.
          initialEvents: features.initial_events || features.initial_screen_progress || [],
        },
      };
    }

    return { type: null, phase: null, data: null };
  }

  // Lightweight bonus detection for reload (used by collectFeatureStates)
  detectBonusFromApi(apiResponse) {
    const { flow, features, outcome } = apiResponse;
    if (
      features?.respins_left === undefined ||
      !features?.respin_game_type ||
      (flow?.state !== "respin" && features?.trigger_screen === undefined)
    ) {
      return null;
    }
    let phase;
    if (flow?.command === "spin") {
      phase = "intro";
    } else if (flow?.command === "respin") {
      phase = "in_progress";
    } else if (flow?.command === "init" && flow?.state === "respin") {
      phase = outcome?.storage ? "in_progress" : "intro";
    } else {
      phase = "intro";
    }
    const storage = outcome?.storage || {};
    return {
      phase,
      data: {
        respinsLeft: features.respins_left,
        respinsIssued: features.respins_issued ?? 3,
        respinGameType: features.respin_game_type,
        triggerScreen: features.trigger_screen,
        triggerCoordinates: features.trigger_coordinates || null,
        initialRespinScreen: normalizeScreenData(features.initial_respin_screen) || null,
        initialRespinValues: features.initial_respin_values || null,
        screenProgress: this._normalizeScreenProgress(
            phase === "intro"
              ? [
                ...(features.initial_events || features.initial_screen_progress || []),
                ...(storage.events || storage.screen_progress || []),
              ]
              : (storage.events || storage.screen_progress || [])
          ),
        coinValues: storage.coin_values || null,
        originalValues: storage.original_values || null,
        initialRespinOriginalValues: features.initial_respin_original_values || null,
        // The activation event log for the trigger spin — used to determine which mystery
        // coins were actually revealed (m_reveals) during the initial activation sequence.
        // Mystery coins NOT in this list are still "raw" and must show as jackpot coins on reload.
        initialEvents: features.initial_events || features.initial_screen_progress || [],
      },
    };
  }

  // ── Screen Progress Helpers ──

  /**
   * Accumulate flippedCoinPositions across respins.
   * Loads the previous set from localStorage, then processes the CURRENT round's
   * events (d_doubles, c_collect) to add any newly flipped positions.
   * For the first save (trigger intro), processes initial_events instead.
   */
  _accumulateFlippedPositions(featureState) {
    const BLANK_ID = 16;
    const data = featureState.data || {};

    // Load previously accumulated flipped positions
    const savedRound = StorageManager.loadBonusGameRound();
    const existing = new Set(savedRound?.flippedCoinPositions || []);

    // Determine which events to process:
    // - First save (no previous round): process initial_events (trigger) + current storage.events
    // - Subsequent saves: only current round's storage.events (prior flips are in localStorage)
    const isFirstSave = !savedRound;
    const rawStorageEvts = data.currentRound?.storage?.events || data.currentRound?.storage?.screen_progress || [];
    const rawEvents = isFirstSave
      ? [...(data.initialEvents || []), ...rawStorageEvts]
      : rawStorageEvts;

    // Normalize events so both cells[] and coordinates[] formats produce [reel, row] pairs
    const events = this._normalizeScreenProgress(rawEvents);

    // Build onBoard: prevScreen (all coins before this spin) + new coins from this round
    const onBoard = new Set();
    if (!isFirstSave) {
      // Seed from prevScreen
      const ps = data.prevScreen || data.currentRound?.storage?.prev_screen || [];
      for (let ri = 0; ri < ps.length; ri++) {
        if (!Array.isArray(ps[ri])) continue;
        for (let si = 0; si < ps[ri].length; si++) {
          const cellId = parseInt(ps[ri][si], 10);
          if (!isNaN(cellId) && cellId !== BLANK_ID) {
            onBoard.add(`${ri}_${si}`);
          }
        }
      }
    }

    // Process normalized events (each has coordinates: [reel, row])
    for (const event of events) {
      const eventType = event.type || event.action;
      const coords = event.coordinates;
      if (!Array.isArray(coords)) continue;
      const pairs = Array.isArray(coords[0]) ? coords : [coords];

      if (eventType === 'new_coins' || eventType === 'm_reveals') {
        for (const p of pairs) {
          if (Array.isArray(p) && p.length >= 2) onBoard.add(`${p[0]}_${p[1]}`);
        }
      } else if (eventType === 'd_doubles') {
        for (const p of pairs) {
          if (Array.isArray(p) && p.length >= 2) existing.add(`${p[0]}_${p[1]}`);
        }
      } else if (eventType === 'c_collect') {
        for (const key of onBoard) existing.add(key);
      }
    }

    const result = [...existing];
    return result;
  }


  // Normalize screen_progress coordinate formats to consistent flat pairs
  // and split batched coordinates into individual actions
  _normalizeScreenProgress(rawProgress) {
    if (!rawProgress || !Array.isArray(rawProgress)) return [];

    const EXPAND_ACTIONS = new Set(["new_coins", "m_reveals", "d_reveals", "c_collect"]);

    const normalized = [];
    for (const rawAction of rawProgress) {
      const actionType = rawAction.type || rawAction.action;

      // ── NEW FORMAT: cells array ──
      if (rawAction.cells && Array.isArray(rawAction.cells)) {
        if (EXPAND_ACTIONS.has(actionType)) {
          // Split each cell into an individual action (1 event per cell)
          for (const cell of rawAction.cells) {
            normalized.push({
              action: actionType,
              coordinates: [cell.reel, cell.row],
              value: cell.value ?? 0,
              ...(cell.tier ? { tier: cell.tier } : {}),
            });
          }
        } else {
          // d_doubles: keep as array of pairs (batch operation)
          normalized.push({
            action: actionType,
            coordinates: rawAction.cells.map(c => [c.reel, c.row]),
            cells: rawAction.cells, // preserve full cells for value access
          });
        }
        continue;
      }

      // ── LEGACY FORMAT: coordinates array (backward compat) ──
      const action = rawAction.type && !rawAction.action
        ? { ...rawAction, action: rawAction.type }
        : rawAction;

      const coords = action.coordinates;
      if (!Array.isArray(coords) || coords.length === 0) {
        normalized.push(action);
        continue;
      }

      if (EXPAND_ACTIONS.has(action.action) && Array.isArray(coords[0])) {
        // Array-of-pairs: [[0,3],[0,4]] → split into individual actions
        for (const coord of coords) {
          normalized.push({ ...action, coordinates: coord });
        }
      } else {
        normalized.push(action);
      }
    }
    return normalized;
  }

  // ── Game Mode ──

  /**
   * Which board a response belongs to.
   *
   * ── The same rule, applied to BOTH features ──
   *
   * detectFeatureState only knows the respin bonus, so a free-spins response
   * used to fall through to "base" — and everything keyed off gameMode then
   * treated the bonus as the base game: the bet was deducted per free spin and
   * the free-spin board was written into game.currentRound.
   *
   * The two features report their phase identically ("intro" then
   * "in_progress"), so the rule below is written once and asked twice. The
   * intro phase is "base" for both, and for the same reason: Pot Collection's
   * intro IS the trigger spin, and free spins' intro covers the trigger, the
   * gamble screen and the entry popups — all played on the base board, all
   * paid for by a real base bet. The round itself is "feature".
   */
  getGameMode(featureState, freeSpinsState = null) {
    if (featureState.type && featureState.phase === "intro") return "base";
    if (featureState.type) return "feature";

    if (freeSpinsState?.phase === "intro") return "base";
    if (freeSpinsState?.phase) return "feature";

    return "base";
  }

  // ── Storage ──

  saveGameState(gameState) {
    const { gameMode, round, bet, balance } = gameState;
    if (gameMode === "base") {
      StorageManager.saveBaseGameRound({ round, bet, balance });
    }
  }

  /**
   * A decision command's ONLY write to storage: the gamble data on the
   * rounds already stored, nothing else.
   *
   * A gamble response carries no board, so it must never SAVE a round —
   * that is how its empty outcome once wiped base_game_round. But the
   * stored trigger round carries gamble data of its own
   * (round.gambleScatter), and a reload replays whatever is stored: left
   * stale, the reveal shows the FIRST offer instead of the rung the ladder
   * actually reached. So each gamble answer patches exactly that field, in
   * both stores, the moment it lands.
   */
  _patchStoredGambleData(features) {
    const gamble = parseGambleState(features);
    if (!Array.isArray(gamble?.current)) return;
    const [value, tier] = gamble.current;

    // The gamble data lives on the FROZEN entry board, which is the half the
    // reveal replays from. The base-game copy is not patched: saveBaseGameRound
    // whitelists its fields and drops gambleScatter, so that half could never
    // hold one and writing it back only stripped what was there.
    const record = StorageManager.loadFreeSpinsRound();
    const trigger = record?.triggerRound;
    if (!trigger?.gambleScatter) return;

    StorageManager.updateFreeSpinsRound({
      triggerRound: {
        ...trigger,
        gambleScatter: {
          ...trigger.gambleScatter,
          value,
          tier,
          variants: gamble.variants ?? [],
        },
      },
      packageTier: tier,
    });
  }

  // ── Reload ──

  collectFeatureStates(apiResponse) {
    return {
      bonus: {
        api: this.detectBonusFromApi(apiResponse),
      },
    };
  }

  determineFeaturePriority(features) {
    if (features.bonus.api) {
      return { primary: "bonus" };
    }
    return { primary: "base" };
  }

  buildBaseGameReload() {
    const savedBaseGame = StorageManager.loadBaseGameRound();
    if (savedBaseGame) {
      return { round: savedBaseGame.round, bet: savedBaseGame.bet, reloadState: { needsReload: false } };
    }
    return { reloadState: { needsReload: false } };
  }

  loadGameState(apiResponse) {
    this.collectFeatureStates(apiResponse);
    // PC reload is handled by potCollectionSlice via featureState
    return this.buildBaseGameReload();
  }

  clearCompletionData() {
    // Clear bonus game round data on first base game spin after bonus
    StorageManager.clearBonusGameRound();
    // Clear free rounds data on first base game spin after FR completion
    StorageManager.clearFreeRoundsRound();
  }

  /**
   * The Random Pot's presentation spin (GDD "Random Pot — Hold & Win
   * Activation"; Games/Shared/randomPot). NOTHING is sent: this is the kept
   * trigger response, same balance / flow / holdAndWin, with only the board
   * replaced — the awarded coins where the server put them (rows 0–3 of the
   * active zone = the base rows), and filler that cannot pay ("Filler symbols
   * must not form regular wins": the low symbols 5/6 on even reels and 7/8 on
   * odd ones, so no line can match two reels running). The coins carry their
   * values and light as the trigger line, which opens the H&W intro exactly
   * as a natural trigger does.
   */
  randomPotPresentation() {
    const kept = this._randomPotTrigger;
    this._randomPotTrigger = null;
    if (!kept) {
      return Promise.reject({ errors: [{ code: 0, desc: "no_random_pot" }] });
    }
    const columns = Settings.REEL_COUNT;
    const rows = Settings.SYMBOLS_VISIBLE;
    const FILLER = [
      [5, 6],
      [7, 8],
    ];
    const reels = Array.from({ length: columns }, (_, c) =>
      Array.from({ length: rows }, (_, r) => FILLER[c % 2][r % 2]),
    );
    const coinCells = [];
    kept.rawNewCells.forEach(([reel, row, , kind]) => {
      if (kind !== "coin" || row >= rows || !reels[reel]) return;
      reels[reel][row] = COIN_SYMBOL_ID;
      coinCells.push([reel, row]);
    });
    const round = {
      reels,
      wins: withTriggerCoinWin([], coinCells),
      win: 0,
      bet: kept.bet,
      winType: "",
      isAnimatedWin: false,
      triggerWin: true,
      storage: null,
      // No anticipation on this board (scatterAnticipation reads it).
      randomPotPresentation: true,
    };
    markJackpotCoins(round, kept.rawNewCells, this.jackpots);
    assignCoinValues(round, kept.rawNewCells, kept.bet, this.baseBet);
    return Promise.resolve({ ...kept.result, round });
  }

  extractRoundFromInit(apiResponse) {
    const screen = apiResponse?.options?.screen;
    if (screen && Array.isArray(screen)) {
      return { reels: normalizeScreenData(screen), wins: [], win: 0 };
    }
    return null;
  }
}

export { spinWorkerCode };

export default PopiPlayRGS;
